import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Menubar from "../Menubar/Menubar";
import { Outlet, useNavigate } from "react-router-dom";
import "../Dashboard/Dashboard.css";
import { Tooltip } from "antd";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdDelete,
  MdHolidayVillage,
  MdKeyboardArrowDown,
} from "react-icons/md";
import { FaEdit, FaEye, FaTrash, FaStar } from "react-icons/fa";
import { RiFileEditFill } from "react-icons/ri";
import { FaFileArrowDown, FaHotel, FaWandMagicSparkles } from "react-icons/fa6";
import { VscPercentage } from "react-icons/vsc";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import useMinimumLoading from "../hooks/Useminimumloading";
import { API_URL } from "../../Config";

const MAX_RANK = 10;
const ALL_RANKS = Array.from({ length: MAX_RANK }, (_, i) => i + 1);

/* ═════════════════════════════════════════════════════════════════════════
   UI TOKENS + SHARED PRESENTATIONAL COMPONENTS  (same set as Inquire Leads)
   ═════════════════════════════════════════════════════════════════════════ */
const ui = {
  card: "bg-white rounded-xl border border-gray-200 shadow-sm",
  th: "px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap",
  td: "px-4 py-3 align-middle",
  btn: "h-9 px-4 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 whitespace-nowrap",
  btnPrimary: "bg-[#1D2826] text-white hover:bg-[#2d3d3a]",
  btnGhost: "bg-gray-100 text-gray-600 hover:bg-gray-200",
  btnDanger: "bg-red-500 text-white hover:bg-red-600",
  btnOutline:
    "border-2 border-[#1D2826] text-[#1D2826] hover:bg-[#1D2826] hover:text-white",
};

function StatCard({ label, value, icon }) {
  return (
    <div
      className={`${ui.card} flex items-center justify-between gap-4 px-4 py-3`}
    >
      <div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
      </div>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#3C4C4E] text-xl text-white">
        {icon}
      </div>
    </div>
  );
}

const ICON_TONES = {
  sky: "border-sky-300 hover:text-sky-500",
  yellow: "border-yellow-300 hover:text-yellow-600",
  purple: "border-purple-300 hover:text-purple-500",
  red: "border-red-300 hover:text-red-500",
};
function IconButton({ tip, tone = "sky", disabled, onClick, children }) {
  return (
    <Tooltip title={tip} placement="top">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-gray-400 transition-all hover:scale-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 ${
          disabled ? "border-gray-200" : ICON_TONES[tone]
        }`}
      >
        {children}
      </button>
    </Tooltip>
  );
}

function Pagination({
  total,
  from,
  to,
  currentPage,
  setCurrentPage,
  totalPages,
  pages,
}) {
  const nav =
    "flex h-8 min-w-8 items-center justify-center px-2 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent";
  return (
    <nav className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-4 py-2 md:flex-row">
      <span className="text-xs text-gray-500">
        Showing{" "}
        <b className="text-gray-700">
          {total ? from : 0}–{to}
        </b>{" "}
        of <b className="text-gray-700">{total}</b>
      </span>
      <ul className="flex items-center gap-1">
        <li>
          <button
            className={`${nav} rounded`}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          >
            <MdKeyboardDoubleArrowLeft />
          </button>
        </li>
        {pages.map((p, i) =>
          p === "..." ? (
            <li key={i} className="px-1 text-gray-400">
              …
            </li>
          ) : (
            <li key={i}>
              <button
                onClick={() => setCurrentPage(p)}
                className={`${nav} rounded ${
                  currentPage === p
                    ? "bg-[#1D2826] text-white hover:bg-[#1D2826]"
                    : ""
                }`}
              >
                {p}
              </button>
            </li>
          ),
        )}
        <li>
          <button
            className={`${nav} rounded`}
            disabled={currentPage === totalPages || !totalPages}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          >
            <MdKeyboardDoubleArrowRight />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function TableShell({ head, children, footer }) {
  return (
    <section className={`${ui.card} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>{head}</tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
      {footer}
    </section>
  );
}

function EmptyRow({ colSpan, children }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-4 py-14 text-center text-sm text-gray-400"
      >
        {children}
      </td>
    </tr>
  );
}

const rowCls = "border-b border-gray-100 transition-colors hover:bg-gray-50";

/* ═════════════════════════════════════════════════════════════════════════
   RANKING / PROGRESS / RATING WIDGETS
   ═════════════════════════════════════════════════════════════════════════ */
const RankChips = ({ title, value, availableRanks, onSelect }) => {
  const current = Number(value) || 0;
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </span>
        {current > 0 && (
          <button
            type="button"
            onClick={() => onSelect(0)}
            className="text-[11px] text-red-500 hover:underline"
          >
            Remove
          </button>
        )}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {ALL_RANKS.map((r) => {
          const isCurrent = r === current;
          const taken = !isCurrent && !availableRanks.includes(r);
          return (
            <button
              key={r}
              type="button"
              disabled={taken}
              title={taken ? "Already taken" : `Set rank ${r}`}
              onClick={() => !isCurrent && onSelect(r)}
              className={`h-8 rounded-md border text-xs font-medium transition-colors ${
                isCurrent
                  ? "border-green-500 bg-green-500 text-white"
                  : taken
                    ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 line-through"
                    : "border-gray-200 bg-white text-gray-700 hover:border-green-500 hover:text-green-600"
              }`}
            >
              {r}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const RankPopover = ({
  name,
  topValue,
  typeValue,
  typeLabel,
  availableTop,
  availableType,
  onSelectTop,
  onSelectType,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const top = Number(topValue) || 0;
  const typeRank = Number(typeValue) || 0;
  const hasRank = top > 0 || typeRank > 0;

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const outside = (e) =>
      !btnRef.current?.contains(e.target) &&
      !menuRef.current?.contains(e.target) &&
      close();
    const onScroll = (e) => !menuRef.current?.contains(e.target) && close();
    document.addEventListener("mousedown", outside);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", outside);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const toggle = () => {
    if (!open) {
      const r = btnRef.current.getBoundingClientRect();
      const openUp = window.innerHeight - r.bottom < 300;
      setPos(
        openUp
          ? { bottom: window.innerHeight - r.top + 4, left: r.left }
          : { top: r.bottom + 4, left: r.left },
      );
    }
    setOpen((o) => !o);
  };

  return (
    <>
      <div className="flex min-w-[200px] items-center gap-2">
        <Tooltip title={name} placement="left">
          <span className="max-w-[240px] truncate font-semibold text-gray-900">
            {name}
          </span>
        </Tooltip>

        {top > 0 && (
          <span className="whitespace-nowrap rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-600">
            Top {top}
          </span>
        )}
        {typeRank > 0 && (
          <span className="whitespace-nowrap rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-600">
            #{typeRank}
          </span>
        )}

        <button
          ref={btnRef}
          type="button"
          onClick={toggle}
          title="Set rankings"
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors ${
            hasRank
              ? "bg-green-500 text-white hover:bg-green-600"
              : "bg-gray-200 text-gray-500 hover:bg-gray-300"
          }`}
        >
          <MdKeyboardArrowDown
            size={16}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <div
          ref={menuRef}
          style={{ position: "fixed", ...pos }}
          className="z-[100] w-64 space-y-3 rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
        >
          <RankChips
            title="Top 10 Property"
            value={topValue}
            availableRanks={availableTop}
            onSelect={onSelectTop}
          />
          <div className="border-t border-gray-100" />
          <RankChips
            title={typeLabel}
            value={typeValue}
            availableRanks={availableType}
            onSelect={onSelectType}
          />
        </div>
      )}
    </>
  );
};

const ProgressBar = ({ value }) => {
  const getColor = () => {
    if (value <= 50) return "bg-red-500";
    if (value < 100) return "bg-yellow-300";
    return "bg-green-500";
  };
  const getTextColor = () => {
    if (value <= 50) return "text-red-500";
    if (value < 100) return "text-yellow-500";
    return "text-green-500";
  };

  return (
    <div className="flex w-20 flex-col gap-1">
      <div className={`text-sm font-semibold leading-none ${getTextColor()}`}>
        {value}%
      </div>
      <div className="h-2 w-full rounded-full bg-gray-200">
        <div
          className={`h-2 rounded-full ${getColor()} transition-all duration-500`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

const StarRating = ({ rating = 0, onRate }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onRate(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110 focus:outline-none"
        >
          <FaStar
            size={16}
            className={
              (hover || rating) >= star ? "text-yellow-400" : "text-gray-300"
            }
          />
        </button>
      ))}
    </div>
  );
};

/* Live / Disabled pill (same markup as before, tooltip prop fixed: antd uses `title`) */
function AvailabilityPill({ item, onToggle }) {
  return (
    <Tooltip
      title={item.availability ? "Click to Disable" : "Click to Make Live"}
      placement="left"
    >
      <button
        onClick={() => onToggle(item)}
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 transition-all duration-300 hover:scale-105 ${
          item.availability
            ? "border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
            : "border-gray-200 bg-gray-100 hover:bg-gray-200"
        }`}
      >
        <span
          className={`h-2 w-2 rounded-full ${
            item.availability ? "animate-pulse bg-emerald-500" : "bg-gray-400"
          }`}
        />
        <span
          className={`text-xs font-semibold ${
            item.availability ? "text-emerald-700" : "text-gray-600"
          }`}
        >
          {item.availability ? "Live" : "Disabled"}
        </span>
      </button>
    </Tooltip>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT  (state + handlers: unchanged)
   ═════════════════════════════════════════════════════════════════════════ */
const Properties = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");

    if (savedState !== null) {
      return JSON.parse(savedState);
    }

    return window.innerWidth >= 1024;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const showLoader = useMinimumLoading(isLoading);
  const [table_property_villa, setTableProperty_villa] = useState([]);
  const [table_property_hotel, setTableProperty_hotel] = useState([]);
  const [table_property_resort, setTableProperty_resort] = useState([]);
  const [table_property_apartment, setTableProperty_apartment] = useState([]);
  const [villaProgress, setVillaProgress] = useState({});
  const [hotelProgress, setHotelProgress] = useState({});

  // Villa pagination
  const rowsPerPage_villa = 20;
  const [currentPage_villa, setCurrentPage_villa] = useState(() => {
    const saved = sessionStorage.getItem("villaPage");
    return saved ? parseInt(saved, 10) : 1;
  });

  const filtered_property_villa = table_property_villa.filter((villa) => {
    const query = searchQuery.toLowerCase();

    const progress = villaProgress[villa.order]?.toString() || "";

    return [
      villa.customer,
      villa.propertyowner,
      villa.location,
      villa.propertycode,
      progress,
    ].some((field) => field?.toLowerCase().includes(query));
  });
  // Apply custom sorting
  const sorted_property_villa = [...filtered_property_villa].sort((a, b) => {
    const aIsToggled = a.topPropertyEnabled || a.topFiveEnabled;
    const bIsToggled = b.topPropertyEnabled || b.topFiveEnabled;

    if (aIsToggled === bIsToggled) {
      return b.order - a.order;
    }

    return aIsToggled && !bIsToggled ? -1 : 1;
  });

  // Apply pagination
  const indexOfLastTable_villa = currentPage_villa * rowsPerPage_villa;
  const indexOfFirstTable_villa = indexOfLastTable_villa - rowsPerPage_villa;

  const current_property_villa = sorted_property_villa.slice(
    indexOfFirstTable_villa,
    indexOfLastTable_villa,
  );

  const totalPages_villa = Math.ceil(
    sorted_property_villa.length / rowsPerPage_villa,
  );

  const generatePageNumbers_villa = () => {
    const pages = [];
    if (totalPages_villa <= 3) {
      for (let i = 1; i <= totalPages_villa; i++) pages.push(i);
    } else {
      if (currentPage_villa > 2) {
        pages.push(1, "...");
      }
      for (let i = currentPage_villa - 1; i <= currentPage_villa + 1; i++) {
        if (i > 0 && i <= totalPages_villa) pages.push(i);
      }
      if (currentPage_villa < totalPages_villa - 1) {
        pages.push("...", totalPages_villa);
      }
    }
    return pages;
  };

  // ---------------- Hotels ----------------
  const rowsPerPage_hotel = 20;
  const [currentPage_hotel, setCurrentPage_hotel] = useState(1);
  const filtered_property_hotel = table_property_hotel.filter((hotel) => {
    const query = searchQuery.toLowerCase();

    const progress = hotelProgress[hotel.order]?.toString() || "";

    return [
      hotel.customer,
      hotel.propertyowner,
      hotel.location,
      hotel.propertycode,
      progress,
    ].some((field) => field?.toLowerCase().includes(query));
  });

  // Apply custom sorting for hotels
  const sorted_property_hotel = [...filtered_property_hotel].sort((a, b) => {
    const aIsToggled = a.topPropertyEnabled || a.topFiveEnabled;
    const bIsToggled = b.topPropertyEnabled || b.topFiveEnabled;

    if (aIsToggled === bIsToggled) {
      return b.order - a.order;
    }

    return aIsToggled && !bIsToggled ? -1 : 1;
  });

  const indexOfLastTable_hotel = currentPage_hotel * rowsPerPage_hotel;
  const indexOfFirstTable_hotel = indexOfLastTable_hotel - rowsPerPage_hotel;
  const current_property_hotel = sorted_property_hotel.slice(
    indexOfFirstTable_hotel,
    indexOfLastTable_hotel,
  );
  const totalPages_hotel = Math.ceil(
    sorted_property_hotel.length / rowsPerPage_hotel,
  );

  const generatePageNumbers_hotel = () => {
    const pageNumbers = [];
    if (totalPages_hotel <= 3) {
      for (let i = 1; i <= totalPages_hotel; i++) pageNumbers.push(i);
    } else {
      if (currentPage_hotel > 2) {
        pageNumbers.push(1);
        pageNumbers.push("...");
      }
      for (let i = currentPage_hotel - 1; i <= currentPage_hotel + 1; i++) {
        if (i > 0 && i <= totalPages_hotel) pageNumbers.push(i);
      }
      if (currentPage_hotel < totalPages_hotel - 1) {
        pageNumbers.push("...");
        pageNumbers.push(totalPages_hotel);
      }
    }
    return pageNumbers;
  };

  // ---------------- Resorts ----------------
  const rowsPerPage_resort = 20;
  const [currentPage_resort, setCurrentPage_resort] = useState(1);

  const filtered_property_resort = table_property_resort.filter((resort) =>
    [resort.customer, resort.propertyowner, resort.location].some((field) =>
      field?.toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  );

  // Apply custom sorting for resorts
  const sorted_property_resort = [...filtered_property_resort].sort((a, b) => {
    const aIsToggled = a.topPropertyEnabled || a.topFiveEnabled;
    const bIsToggled = b.topPropertyEnabled || b.topFiveEnabled;

    if (aIsToggled === bIsToggled) {
      return b.order - a.order;
    }

    return aIsToggled && !bIsToggled ? -1 : 1;
  });

  const indexOfLastTable_resort = currentPage_resort * rowsPerPage_resort;
  const indexOfFirstTable_resort = indexOfLastTable_resort - rowsPerPage_resort;
  const current_property_resort = sorted_property_resort.slice(
    indexOfFirstTable_resort,
    indexOfLastTable_resort,
  );
  const totalPages_resort = Math.ceil(
    sorted_property_resort.length / rowsPerPage_resort,
  );

  const handlePageChange_resort = (pageNumber) =>
    setCurrentPage_resort(pageNumber);
  const handleNextPage_resort = () => {
    if (currentPage_resort < totalPages_resort)
      setCurrentPage_resort((p) => p + 1);
  };
  const handlePrevPage_resort = () => {
    if (currentPage_resort > 1) setCurrentPage_resort((p) => p - 1);
  };
  const generatePageNumbers_resort = () => {
    const pageNumbers = [];
    if (totalPages_resort <= 3) {
      for (let i = 1; i <= totalPages_resort; i++) pageNumbers.push(i);
    } else {
      if (currentPage_resort > 2) {
        pageNumbers.push(1);
        pageNumbers.push("...");
      }
      for (let i = currentPage_resort - 1; i <= currentPage_resort + 1; i++) {
        if (i > 0 && i <= totalPages_resort) pageNumbers.push(i);
      }
      if (currentPage_resort < totalPages_resort - 1) {
        pageNumbers.push("...");
        pageNumbers.push(totalPages_resort);
      }
    }
    return pageNumbers;
  };

  // ---------------- Apartments ----------------
  const rowsPerPage_apartment = 20;
  const [currentPage_apartment, setCurrentPage_apartment] = useState(1);

  const filtered_property_apartment = table_property_apartment.filter(
    (apartment) =>
      [apartment.customer, apartment.propertyowner, apartment.location].some(
        (field) => field?.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
  );

  // Apply custom sorting for apartments
  const sorted_property_apartment = [...filtered_property_apartment].sort(
    (a, b) => {
      const aIsToggled = a.topPropertyEnabled || a.topFiveEnabled;
      const bIsToggled = b.topPropertyEnabled || b.topFiveEnabled;

      if (aIsToggled === bIsToggled) {
        return b.order - a.order;
      }

      return aIsToggled && !bIsToggled ? -1 : 1;
    },
  );

  const indexOfLastTable_apartment =
    currentPage_apartment * rowsPerPage_apartment;
  const indexOfFirstTable_apartment =
    indexOfLastTable_apartment - rowsPerPage_apartment;
  const current_property_apartment = sorted_property_apartment.slice(
    indexOfFirstTable_apartment,
    indexOfLastTable_apartment,
  );
  const totalPages_apartment = Math.ceil(
    sorted_property_apartment.length / rowsPerPage_apartment,
  );

  const handlePageChange_apartment = (pageNumber) =>
    setCurrentPage_apartment(pageNumber);
  const handleNextPage_apartment = () => {
    if (currentPage_apartment < totalPages_apartment)
      setCurrentPage_apartment((p) => p + 1);
  };
  const handlePrevPage_apartment = () => {
    if (currentPage_apartment > 1) setCurrentPage_apartment((p) => p - 1);
  };
  const generatePageNumbers_apartment = () => {
    const pageNumbers = [];
    if (totalPages_apartment <= 3) {
      for (let i = 1; i <= totalPages_apartment; i++) pageNumbers.push(i);
    } else {
      if (currentPage_apartment > 2) {
        pageNumbers.push(1);
        pageNumbers.push("...");
      }
      for (
        let i = currentPage_apartment - 1;
        i <= currentPage_apartment + 1;
        i++
      ) {
        if (i > 0 && i <= totalPages_apartment) pageNumbers.push(i);
      }
      if (currentPage_apartment < totalPages_apartment - 1) {
        pageNumbers.push("...");
        pageNumbers.push(totalPages_apartment);
      }
    }
    return pageNumbers;
  };
  useEffect(() => {
    const fetchAllProgress = async () => {
      try {
        const res = await fetch(`${API_URL}/property-completion-all`);

        const data = await res.json();

        // data format: {1: 72.6, 2: 80.4, 3: 69.4}

        setVillaProgress(data);
        setHotelProgress(data);
      } catch (err) {
        console.error("Failed to fetch progress:", err);
      }
    };

    fetchAllProgress();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navigate = useNavigate();

  const [, setShowPopup_filter] = useState(false); //showPopup_filter
  const filterRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowPopup_filter(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [active, setActive] = useState("Villas");

  const options = ["Villas", "Hotels"];

  const [selectedRows_apartment, setSelectedRows_apartment] = useState([]);

  const [selectedRows_villa, setSelectedRows_villa] = useState([]);

  const handleRowCheckboxChange_villa = (order) => {
    setSelectedRows_villa((prevSelected) =>
      prevSelected.includes(order)
        ? prevSelected.filter((id) => id !== order)
        : [...prevSelected, order],
    );
  };

  const handleSelectAll_villa = () => {
    if (selectedRows_villa.length === current_property_villa.length) {
      setSelectedRows_villa([]);
    } else {
      setSelectedRows_villa(
        current_property_villa.map((booking) => booking.order),
      );
    }
  };
  const handleSelectAll_apartment = (e) => {
    if (e.target.checked) {
      setSelectedRows_apartment(current_property_apartment.map((p) => p.id));
    } else {
      setSelectedRows_apartment([]);
    }
  };

  const loadAvailableRanks = async () => {
    try {
      const [g, p] = await Promise.all([
        axios.get(`${API_URL}/getTakenTopFive`),
        axios.get(`${API_URL}/getTakenTopProperty`),
      ]);
      const free = (r) =>
        ALL_RANKS.filter((x) => !(r.data.taken || []).includes(x));
      if (g.data.success) setAvailableRanks(free(g));
      if (p.data.success) setAvailablePropertyRanks(free(p));
    } catch (err) {
      console.error("Failed to load available ranks", err);
    }
  };

  useEffect(() => {
    loadAvailableRanks();
  }, []);

  // Refresh Resort ranks
  const refreshResortRanks = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/getTakenTopProperty?propertytype=resort`,
      );
      if (res.data.success) {
        const allRanks = [1, 2, 3, 4, 5];
        const taken = res.data.taken || [];
        const available = allRanks.filter((r) => !taken.includes(r));
        setAvailableResortRanks(available);
      }
    } catch (err) {
      console.error("Failed to refresh resort ranks", err);
    }
  };

  const [selectedRows_hotel, setSelectedRows_hotel] = useState([]);

  const handleRowCheckboxChange_hotel = (order) => {
    setSelectedRows_hotel((prevSelected) =>
      prevSelected.includes(order)
        ? prevSelected.filter((id) => id !== order)
        : [...prevSelected, order],
    );
  };

  const handleSelectAll_hotel = () => {
    if (selectedRows_hotel.length === current_property_hotel.length) {
      setSelectedRows_hotel([]);
    } else {
      setSelectedRows_hotel(
        current_property_hotel.map((booking) => booking.order),
      );
    }
  };

  const handleToggle_topProperty_hotel = async (order) => {
    const hotel = table_property_hotel.find((item) => item.order === order);
    const newEnabled = !hotel.topPropertyEnabled;

    if (!newEnabled) {
      // Turning OFF → clear in DB
      try {
        const res = await axios.post(`${API_URL}/update-rank`, {
          propertyid: order,
          propertytype: "hotel",
          rank: 0,
        });

        if (res.data.success) {
          const updated = table_property_hotel.map((item) =>
            item.order === order
              ? { ...item, topPropertyEnabled: false, topPropertyValue: "" }
              : item,
          );
          setTableProperty_hotel(updated);
        } else {
          alert(res.data.message);
        }
      } catch (err) {
        console.error("Failed to reset top property (hotel)", err);
        alert("Error updating top property toggle");
      }
    } else {
      // Turning ON → just enable, wait for dropdown
      const updated = table_property_hotel.map((item) =>
        item.order === order
          ? { ...item, topPropertyEnabled: true, topPropertyValue: "" }
          : item,
      );
      setTableProperty_hotel(updated);
    }
  };

  const handleDropdownChange_topProperty_hotel = async (order, value) => {
    try {
      const res = await axios.post(`${API_URL}/update-rank`, {
        propertyid: order,
        propertytype: "hotel",
        rank: parseInt(value, 10), // ensure number
      });

      if (res.data.success) {
        const updated = table_property_hotel.map((item) =>
          item.order === order ? { ...item, topPropertyValue: value } : item,
        );
        setTableProperty_hotel(updated);
        await fetchProperties(); // refresh Hotels table
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      console.error("Failed to update hotel rank", err);
      alert("Error updating hotel rank");
    }
  };

  const fetchProperties = async (type) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/type/${type.toLowerCase()}`);
      const data = res.data.map((item) => ({
        order: item.id,
        customer: item.propertyname,
        propertycode: item.propertycode,
        productName: item.description,
        location: item.location,
        payment: item.currentprice,
        status: "CONFIRMED",
        topPropertyEnabled: item.istopproperty > 0,
        topPropertyValue: item.istopproperty || "",
        topFiveEnabled: item.istopfiverank > 0,
        topFiveValue: item.istopfiverank || "",
        propertyowner: item.propertyowner,
        availability: item.availability,
        rating: item.rating ?? null,
      }));

      if (type === "villa") setTableProperty_villa(data);
      if (type === "hotel") setTableProperty_hotel(data);
      if (type === "resort") setTableProperty_resort(data);
      if (type === "apartment") setTableProperty_apartment(data);
    } catch (err) {
      console.error("Failed to fetch properties", err);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchVillaStoreProperties = async () => {
    setIsLoading(true);

    try {
      const res = await axios.get(`${API_URL}/villa-store`);

      const data = res.data.map((item) => ({
        order: item.id,
        customer: item.propertyname,
        productName: item.description,
        location: item.location,
        payment: item.currentprice,
        status: "CONFIRMED",

        topPropertyEnabled: item.istopproperty > 0,
        topPropertyValue: item.istopproperty || "",

        topFiveEnabled: item.istopfiverank > 0,
        topFiveValue: item.istopfiverank || "",

        propertyowner: item.propertyowner,
        propertycode: item.propertycode,
        availability: item.availability,
        rating: item.rating ?? null,

        // VERY IMPORTANT
        propertytype: item.propertytype,
      }));

      setTableProperty_villa(data);
    } catch (err) {
      console.error("Failed to fetch Villa Store properties", err);
    } finally {
      setIsLoading(false);
    }
  };
  const isFirstMount = useRef(true);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      if (active === "Villas") fetchVillaStoreProperties();
      if (active === "Hotels") fetchProperties("hotel");
      if (active === "Resorts") fetchProperties("resort");
      if (active === "Apartments") fetchProperties("apartment");
      return;
    }

    if (active === "Villas") {
      fetchVillaStoreProperties();
    }
    if (active === "Hotels") {
      setCurrentPage_hotel(1);
      fetchProperties("hotel");
    }
    if (active === "Resorts") {
      setCurrentPage_resort(1);
      fetchProperties("resort");
    }
    if (active === "Apartments") {
      setCurrentPage_apartment(1);
      fetchProperties("apartment");
    }
  }, [active]);

  useEffect(() => {
    if (searchQuery !== "") {
      if (active === "Villas") setCurrentPage_villa(1);
      if (active === "Hotels") setCurrentPage_hotel(1);
      if (active === "Resorts") setCurrentPage_resort(1);
      if (active === "Apartments") setCurrentPage_apartment(1);
    }
  }, [searchQuery]);
  const [selectedRows_resort, setSelectedRows_resort] = useState([]);

  const handleRowCheckboxChange_resort = (order) => {
    setSelectedRows_resort((prevSelected) =>
      prevSelected.includes(order)
        ? prevSelected.filter((id) => id !== order)
        : [...prevSelected, order],
    );
  };

  const handleSelectAll_resort = () => {
    if (selectedRows_resort.length === current_property_resort.length) {
      setSelectedRows_resort([]);
    } else {
      setSelectedRows_resort(
        current_property_resort.map((booking) => booking.order),
      );
    }
  };

  const handleToggle_topProperty_resort = async (order) => {
    const resort = table_property_resort.find((item) => item.order === order);
    const newEnabled = !resort.topPropertyEnabled;

    if (!newEnabled) {
      // Turning OFF → clear in DB
      try {
        const res = await axios.post(`${API_URL}/update-rank`, {
          propertyid: order,
          propertytype: "resort",
          rank: 0,
        });

        if (res.data.success) {
          const updated = table_property_resort.map((item) =>
            item.order === order
              ? { ...item, topPropertyEnabled: false, topPropertyValue: "" }
              : item,
          );
          setTableProperty_resort(updated);
          await refreshResortRanks();
        } else {
          alert(res.data.message);
        }
      } catch (err) {
        console.error("Failed to reset top property (resort)", err);
        alert("Error updating top property toggle");
      }
    } else {
      // Turning ON → just enable, wait for dropdown
      const updated = table_property_resort.map((item) =>
        item.order === order
          ? { ...item, topPropertyEnabled: true, topPropertyValue: "" }
          : item,
      );
      setTableProperty_resort(updated);
    }
  };

  const handleDropdownChange_topProperty_resort = async (order, value) => {
    try {
      const res = await axios.post(`${API_URL}/update-rank`, {
        propertyid: order,
        propertytype: "resort",
        rank: parseInt(value, 10), // ensure number
      });

      if (res.data.success) {
        const updated = table_property_resort.map((item) =>
          item.order === order ? { ...item, topPropertyValue: value } : item,
        );
        setTableProperty_resort(updated);
        await fetchProperties(); // refresh Resorts table
        await refreshResortRanks();
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      console.error("Failed to update resort rank", err);
      alert("Error updating resort rank");
    }
  };

  /* ── Confirmation dialogs (rendered through react-confirm-alert) ───────── */
  const RatingConfirmation = ({ onClose, onConfirm, rating, propertyname }) => {
    const [isSaving, setIsSaving] = useState(false);

    const handleConfirm = async () => {
      setIsSaving(true);
      await onConfirm();
      setIsSaving(false);
      onClose();
    };

    return (
      <div className="w-[min(92vw,420px)] rounded-2xl bg-white p-6 text-center shadow-2xl">
        <h2 className="mb-3 text-base font-bold text-gray-800">
          Confirm rating
        </h2>
        <p className="mb-3 text-sm text-gray-600">
          Set rating for{" "}
          <span className="font-semibold text-gray-900">{propertyname}</span>{" "}
          to:
        </p>
        <div className="mb-6 flex justify-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <FaStar
              key={star}
              size={22}
              className={star <= rating ? "text-yellow-400" : "text-gray-300"}
            />
          ))}
        </div>

        <div className="flex justify-center gap-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className={`${ui.btn} ${ui.btnGhost}`}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSaving}
            className={`${ui.btn} ${ui.btnPrimary}`}
          >
            {isSaving ? "Saving..." : "Confirm"}
          </button>
        </div>
      </div>
    );
  };
  const DeleteConfirmation = ({ onClose, propertyid }) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
      setIsDeleting(true);
      try {
        const url =
          active === "Hotels"
            ? `${API_URL}/deleteHotel/${propertyid}`
            : `${API_URL}/${propertyid}`;

        const res = await axios.delete(url);

        if (res.data.success) {
          toast.success("Property deleted successfully 🗑️");
          if (active === "Villas") fetchProperties("villa");
          if (active === "Hotels") fetchProperties("hotel");
          if (active === "Resorts") fetchProperties("resort");
        } else {
          toast.error(res.data.error || "Failed to delete");
        }
      } catch (err) {
        console.error("Delete error:", err);
        toast.error("Error deleting property");
      } finally {
        setIsDeleting(false);
        onClose();
      }
    };

    return (
      <div className="w-[min(92vw,420px)] rounded-2xl bg-white p-6 text-center shadow-2xl">
        <h2 className="mb-2 text-base font-bold text-gray-800">
          Confirm deletion
        </h2>
        <p className="mb-6 text-sm text-gray-600">
          Are you sure you want to delete this property?
        </p>

        <div className="flex justify-center gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className={`${ui.btn} ${ui.btnGhost}`}
          >
            Keep it
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className={`${ui.btn} ${ui.btnDanger}`}
          >
            {isDeleting ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Deleting...
              </>
            ) : (
              <>
                <FaTrash /> Delete
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteProperty = (propertyid) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <DeleteConfirmation onClose={onClose} propertyid={propertyid} />
      ),
    });
  };

  const handleToggle_topFive_resort = async (order) => {
    const resort = table_property_resort.find((item) => item.order === order);
    const newEnabled = !resort.topFiveEnabled;

    if (!newEnabled) {
      try {
        const res = await axios.post(`${API_URL}/update-rank`, {
          propertyid: order,
          propertytype: "resort",
          rank: 0,
          mode: "topFive",
        });
        if (res.data.success) {
          setTableProperty_resort((prev) =>
            prev.map((i) =>
              i.order === order
                ? { ...i, topFiveEnabled: false, topFiveValue: "" }
                : i,
            ),
          );
        }
      } catch (err) {
        console.error("Failed to reset global top 5 (resort)", err);
      }
    } else {
      setTableProperty_resort((prev) =>
        prev.map((i) =>
          i.order === order
            ? { ...i, topFiveEnabled: true, topFiveValue: "" }
            : i,
        ),
      );
    }
  };

  const handleDropdownChange_topFive_resort = async (order, value) => {
    try {
      const res = await axios.post(`${API_URL}/update-rank`, {
        propertyid: order,
        propertytype: "resort",
        rank: parseInt(value, 10),
        mode: "topFive",
      });

      if (res.data.success) {
        setTableProperty_resort((prev) =>
          prev.map((i) =>
            i.order === order ? { ...i, topFiveValue: value } : i,
          ),
        );
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      console.error("Failed to update global top 5 rank (resort)", err);
    }
  };
  const [availableRanks, setAvailableRanks] = useState(ALL_RANKS);
  const [availablePropertyRanks, setAvailablePropertyRanks] =
    useState(ALL_RANKS);
  const [availableResortRanks, setAvailableResortRanks] = useState([ALL_RANKS]);

  useEffect(() => {
    if (active === "Resorts") {
      axios
        .get(`${API_URL}/getTakenTopProperty?propertytype=resort`)
        .then((res) => {
          if (res.data.success) {
            const allRanks = [1, 2, 3, 4, 5];
            const taken = res.data.taken || [];
            const available = allRanks.filter((r) => !taken.includes(r));
            setAvailableResortRanks(available);
          }
        })
        .catch((err) =>
          console.error("Failed to fetch available resort ranks", err),
        );
    }
  }, [active]);
  const [availableApartmentRanks, setAvailableApartmentRanks] = useState([
    1, 2, 3, 4, 5,
  ]);

  useEffect(() => {
    if (active === "Apartments") {
      axios
        .get(`${API_URL}/getTakenTopProperty?propertytype=apartment`)
        .then((res) => {
          if (res.data.success) {
            const allRanks = [1, 2, 3, 4, 5];
            const taken = res.data.taken || [];
            const available = allRanks.filter((r) => !taken.includes(r));
            setAvailableApartmentRanks(available);
          }
        })
        .catch((err) =>
          console.error("Failed to fetch available apartment ranks", err),
        );
    }
  }, [active]);

  const handleToggle_topProperty_apartment = async (order) => {
    const apartment = table_property_apartment.find(
      (item) => item.order === order,
    );
    const newEnabled = !apartment.topPropertyEnabled;

    if (!newEnabled) {
      // Turning OFF → clear in DB
      try {
        const res = await axios.post(`${API_URL}/update-rank`, {
          propertyid: order,
          propertytype: "apartment",
          rank: 0,
        });

        if (res.data.success) {
          const updated = table_property_apartment.map((item) =>
            item.order === order
              ? { ...item, topPropertyEnabled: false, topPropertyValue: "" }
              : item,
          );
          setTableProperty_apartment(updated);
          // Refresh available apartment ranks
          const res2 = await axios.get(
            `${API_URL}/getTakenTopProperty?propertytype=apartment`,
          );
          if (res2.data.success) {
            const allRanks = [1, 2, 3, 4, 5];
            const taken = res2.data.taken || [];
            const available = allRanks.filter((r) => !taken.includes(r));
            setAvailableApartmentRanks(available);
          }
        } else {
          alert(res.data.message);
        }
      } catch (err) {
        console.error("Failed to reset top property (apartment)", err);
        alert("Error updating top property toggle");
      }
    } else {
      // Turning ON → just enable, wait for dropdown
      const updated = table_property_apartment.map((item) =>
        item.order === order
          ? { ...item, topPropertyEnabled: true, topPropertyValue: "" }
          : item,
      );
      setTableProperty_apartment(updated);
    }
  };

  const handleDropdownChange_topProperty_apartment = async (order, value) => {
    try {
      const res = await axios.post(`${API_URL}/update-rank`, {
        propertyid: order,
        propertytype: "apartment",
        rank: parseInt(value, 10),
      });

      if (res.data.success) {
        const updated = table_property_apartment.map((item) =>
          item.order === order ? { ...item, topPropertyValue: value } : item,
        );
        setTableProperty_apartment(updated);
        await fetchProperties(); // refresh Apartments table
        // Refresh available apartment ranks
        const res2 = await axios.get(
          `${API_URL}/getTakenTopProperty?propertytype=apartment`,
        );
        if (res2.data.success) {
          const allRanks = [1, 2, 3, 4, 5];
          const taken = res2.data.taken || [];
          const available = allRanks.filter((r) => !taken.includes(r));
          setAvailableApartmentRanks(available);
        }
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      console.error("Failed to update apartment rank", err);
      alert("Error updating apartment rank");
    }
  };

  const handleToggle_topFive_apartment = async (order) => {
    const apartment = table_property_apartment.find(
      (item) => item.order === order,
    );
    const newEnabled = !apartment.topFiveEnabled;

    if (!newEnabled) {
      try {
        const res = await axios.post(`${API_URL}/update-rank`, {
          propertyid: order,
          propertytype: "apartment",
          rank: 0,
          mode: "topFive",
        });
        if (res.data.success) {
          setTableProperty_apartment((prev) =>
            prev.map((i) =>
              i.order === order
                ? { ...i, topFiveEnabled: false, topFiveValue: "" }
                : i,
            ),
          );
          // Refresh available global ranks
          const res2 = await axios.get(`${API_URL}/getTakenTopFive`);
          if (res2.data.success) {
            const taken = res2.data.taken || [];
            const allRanks = [1, 2, 3, 4, 5];
            const freeRanks = allRanks.filter((r) => !taken.includes(r));
            setAvailableRanks(freeRanks);
          }
        }
      } catch (err) {
        console.error("Failed to reset global top 5 (apartment)", err);
      }
    } else {
      setTableProperty_apartment((prev) =>
        prev.map((i) =>
          i.order === order
            ? { ...i, topFiveEnabled: true, topFiveValue: "" }
            : i,
        ),
      );
    }
  };

  const handleDropdownChange_topFive_apartment = async (order, value) => {
    try {
      const res = await axios.post(`${API_URL}/update-rank`, {
        propertyid: order,
        propertytype: "apartment",
        rank: parseInt(value, 10),
        mode: "topFive",
      });

      if (res.data.success) {
        setTableProperty_apartment((prev) =>
          prev.map((i) =>
            i.order === order ? { ...i, topFiveValue: value } : i,
          ),
        );
        // Refresh available global ranks instead of reloading
        const res2 = await axios.get(`${API_URL}/getTakenTopFive`);
        if (res2.data.success) {
          const taken = res2.data.taken || [];
          const allRanks = [1, 2, 3, 4, 5];
          const freeRanks = allRanks.filter((r) => !taken.includes(r));
          setAvailableRanks(freeRanks);
        }
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      console.error("Failed to update global top 5 rank (apartment)", err);
    }
  };
  const handleAddProperty = () => {
    if (active === "Hotels") {
      navigate("/AddHotelElementor");
    } else {
      navigate("/AddPropertyDetailElementor");
    }
  };
  const [analytics, setAnalytics] = useState({
    totalvillas: 0,
    totalhotels: 0,
    avgscore: 0,
  });

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API_URL}/property-completion-box`);

        if (res.data.success) {
          setAnalytics(res.data.analytics);
        }
      } catch (err) {
        console.error("Error fetching analytics:", err);
      }
    };

    fetchAnalytics();
  }, []);

  // format numbers with commas
  const formatNumber = (num) =>
    new Intl.NumberFormat("en-IN").format(Number(num));

  const handleAvailabilityToggle = async (item) => {
    const newAvailability = item.availability ? 0 : 1;

    // Optimistically update UI first
    const updateState =
      active === "Hotels" ? setTableProperty_hotel : setTableProperty_villa;
    updateState((prev) =>
      prev.map((row) =>
        row.order === item.order
          ? { ...row, availability: newAvailability }
          : row,
      ),
    );

    try {
      const res = await axios.post(`${API_URL}/update-availability`, {
        propertyid: item.order,
        availability: newAvailability,
      });

      if (!res.data.success) {
        // revert on failure
        updateState((prev) =>
          prev.map((row) =>
            row.order === item.order
              ? { ...row, availability: item.availability }
              : row,
          ),
        );
        alert(res.data.message || "Failed to update availability");
      }
    } catch (err) {
      console.error("Failed to update availability", err);
      // revert on error
      updateState((prev) =>
        prev.map((row) =>
          row.order === item.order
            ? { ...row, availability: item.availability }
            : row,
        ),
      );
      alert("Error updating availability");
    }
  };
  const handleRatingChange = async (order, propertytype, rating) => {
    const updateState =
      propertytype === "hotel"
        ? setTableProperty_hotel
        : setTableProperty_villa;

    // optimistic UI update
    updateState((prev) =>
      prev.map((item) => (item.order === order ? { ...item, rating } : item)),
    );

    try {
      const res = await axios.post(`${API_URL}/update-rating`, {
        propertyid: order,
        rating,
      });
      if (res.data.success) {
        toast.success("Rating updated successfully ⭐");
      } else {
        alert(res.data.message || "Failed to update rating");
        updateState((prev) =>
          prev.map((item) =>
            item.order === order ? { ...item, rating: item.rating } : item,
          ),
        );
      }
    } catch (err) {
      console.error("Failed to update rating", err);
      alert("Error updating rating");
    }
  };
  const confirmRatingChange = (order, propertytype, rating, propertyname) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <RatingConfirmation
          onClose={onClose}
          rating={rating}
          propertyname={propertyname}
          onConfirm={() => handleRatingChange(order, propertytype, rating)}
        />
      ),
    });
  };
  const handleSetRank = async (order, type, mode, rank) => {
    const setTable =
      type === "hotel" ? setTableProperty_hotel : setTableProperty_villa;
    const isGlobal = mode === "topfive";

    try {
      const res = await axios.post(`${API_URL}/update-rank`, {
        propertyid: order,
        propertytype: type,
        rank,
        mode,
      });

      if (!res.data.success) {
        toast.error(res.data.message || "Failed to update rank");
        return;
      }

      setTable((prev) =>
        prev.map((i) => {
          if (i.order !== order) return i;
          return isGlobal
            ? {
                ...i,
                topFiveEnabled: rank > 0,
                topFiveValue: rank > 0 ? rank : "",
              }
            : {
                ...i,
                topPropertyEnabled: rank > 0,
                topPropertyValue: rank > 0 ? rank : "",
              };
        }),
      );
      toast.success(rank > 0 ? `Rank set to ${rank}` : "Rank removed");
      loadAvailableRanks();
    } catch (err) {
      // axios throws on 400, so the "already taken" message lives here
      toast.error(err.response?.data?.message || "Error updating rank");
    }
  };

  const villaPagination = {
    total: filtered_property_villa.length,
    from: indexOfFirstTable_villa + 1,
    to: Math.min(indexOfLastTable_villa, filtered_property_villa.length),
    currentPage: currentPage_villa,
    setCurrentPage: setCurrentPage_villa,
    totalPages: totalPages_villa,
    pages: generatePageNumbers_villa(),
  };
  const hotelPagination = {
    total: filtered_property_hotel.length,
    from: indexOfFirstTable_hotel + 1,
    to: Math.min(indexOfLastTable_hotel, filtered_property_hotel.length),
    currentPage: currentPage_hotel,
    setCurrentPage: setCurrentPage_hotel,
    totalPages: totalPages_hotel,
    pages: generatePageNumbers_hotel(),
  };

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      <main
        className={`min-w-0 flex-1 p-4 transition-all duration-500 sm:p-6 ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] flex-col gap-5">
          <Outlet />

          {/* Header */}
          <header className="flex flex-wrap items-center justify-between gap-3 pl-12 lg:pl-0">
            <h1 className="text-xl font-semibold text-gray-800">Properties</h1>

            <div className="group mr-2 flex h-8 w-8 items-center overflow-hidden rounded-full p-2 duration-300 hover:w-[160px] hover:border hover:border-[#1D2826] hover:shadow-[2px_2px_20px_rgba(0,0,0,0.08)] sm:hover:w-[250px]">
              <div className="flex items-center justify-center fill-[#1D2826]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                >
                  <path d="M18.9,16.776A10.539,10.539,0,1,0,16.776,18.9l5.1,5.1L24,21.88ZM10.5,18A7.5,7.5,0,1,1,18,10.5,7.507,7.507,0,0,1,10.5,18Z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={`Search In ${active}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border-none bg-transparent px-2 text-sm font-normal outline-none focus:ring-0"
              />
            </div>
          </header>

          {/* Stats */}
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Total villas"
              value={formatNumber(analytics.totalvillas)}
              icon={<MdHolidayVillage />}
            />
            <StatCard
              label="Total hotels"
              value={formatNumber(analytics.totalhotels)}
              icon={<FaHotel />}
            />
            <StatCard
              label="Average score"
              value={`${analytics.avgscore}%`}
              icon={<VscPercentage />}
            />
          </section>

          {/* Tabs + actions in ONE card */}
          <section
            className={`${ui.card} flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between`}
          >
            <div className="inline-flex w-fit overflow-hidden rounded-lg border border-gray-300 text-sm font-medium">
              {options.map((option) => (
                <button
                  key={option}
                  onClick={() => setActive(option)}
                  className={`px-4 py-2 transition-colors ${
                    active === option
                      ? "bg-[#1D2826] text-white"
                      : "bg-white text-gray-500 hover:bg-green-50"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => window.open("/SmartScheduler", "_blank")}
                className={`${ui.btn} ${ui.btnOutline}`}
              >
                <FaWandMagicSparkles /> <span>Smart Scheduler</span>
              </button>
              <button
                onClick={handleAddProperty}
                className={`${ui.btn} ${ui.btnPrimary}`}
              >
                + Add property
              </button>
            </div>
          </section>

          {/* ───────────────────────── VILLAS ───────────────────────── */}
          {active === "Villas" && (
            <TableShell
              footer={<Pagination {...villaPagination} />}
              head={
                <>
                  <th className={`${ui.th} w-10`}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll_villa}
                      checked={
                        selectedRows_villa.length ===
                          current_property_villa.length &&
                        current_property_villa.length > 0
                      }
                    />
                  </th>
                  <th className={ui.th}>Villa name</th>
                  <th className={ui.th}>Property type</th>
                  <th className={ui.th}>Property owner</th>
                  <th className={ui.th}>Property Code</th>
                  <th className={ui.th}>Location</th>
                  <th className={ui.th}>Rating</th>
                  <th className={ui.th}>Availability</th>
                  <th className={ui.th}>Score</th>
                  <th className={ui.th}>Actions</th>
                </>
              }
            >
              {showLoader ? (
                <tr>
                  <td colSpan={9}>
                    <Loader
                      label="Loading..."
                      fullScreen={false}
                      className="py-6"
                    />
                  </td>
                </tr>
              ) : current_property_villa.length === 0 ? (
                <EmptyRow colSpan={9}>No properties in this category</EmptyRow>
              ) : (
                current_property_villa.map((villa) => (
                  <tr key={villa.order} className={rowCls}>
                    <td className={ui.td}>
                      <input
                        type="checkbox"
                        onChange={() =>
                          handleRowCheckboxChange_villa(villa.order)
                        }
                        checked={selectedRows_villa.includes(villa.order)}
                      />
                    </td>

                    <td className={ui.td} data-full-content={villa.customer}>
                      <RankPopover
                        name={villa.customer}
                        topValue={villa.topFiveValue}
                        typeValue={villa.topPropertyValue}
                        typeLabel="Villa Rank"
                        availableTop={availableRanks}
                        availableType={availablePropertyRanks}
                        onSelectTop={(rank) => {
                          if (
                            villa.propertytype?.toLowerCase() === "apartment"
                          ) {
                            toast.info("You cannot update apartment rank.");
                            return;
                          }
                          handleSetRank(villa.order, "villa", "topfive", rank);
                        }}
                        onSelectType={(rank) => {
                          if (
                            villa.propertytype?.toLowerCase() === "apartment"
                          ) {
                            toast.info("You cannot update apartment rank.");
                            return;
                          }
                          handleSetRank(villa.order, "villa", "property", rank);
                        }}
                      />
                    </td>

                    <td className={`${ui.td} whitespace-nowrap`}>
                      <Tooltip title={villa.propertytype} placement="left">
                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium capitalize text-gray-600">
                          {villa.propertytype || "—"}
                        </span>
                      </Tooltip>
                    </td>

                    <td className={ui.td}>
                      <Tooltip
                        title={villa.propertyowner || "N/A"}
                        placement="left"
                      >
                        <span className="block max-w-[180px] truncate">
                          {villa.propertyowner || "N/A"}
                        </span>
                      </Tooltip>
                    </td>
                    <td className={ui.td}>
                      <Tooltip
                        title={villa.propertycode || "N/A"}
                        placement="left"
                      >
                        <span className="block max-w-[180px] truncate">
                          {villa.propertycode || "N/A"}
                        </span>
                      </Tooltip>
                    </td>

                    <td className={ui.td}>
                      <Tooltip title={villa.location || "N/A"} placement="left">
                        <span className="block max-w-[200px] truncate">
                          {villa.location || "N/A"}
                        </span>
                      </Tooltip>
                    </td>

                    <td className={ui.td}>
                      <StarRating
                        rating={villa.rating}
                        onRate={(val) =>
                          confirmRatingChange(
                            villa.order,
                            "villa",
                            val,
                            villa.customer,
                          )
                        }
                      />
                    </td>

                    <td className={ui.td}>
                      <AvailabilityPill
                        item={villa}
                        onToggle={handleAvailabilityToggle}
                      />
                    </td>

                    <td className={ui.td}>
                      <ProgressBar value={villaProgress[villa.order] ?? 0} />
                    </td>

                    <td className={ui.td}>
                      <div className="flex items-center gap-2">
                        <IconButton
                          tip="View property"
                          onClick={() => {
                            sessionStorage.setItem(
                              "villaPage",
                              currentPage_villa,
                            );
                            navigate("/EditPropertyDetailElementor", {
                              state: { propertyid: villa.order, mode: "view" },
                            });
                          }}
                        >
                          <FaEye size={14} />
                        </IconButton>
                        <IconButton
                          tip="Edit property"
                          tone="yellow"
                          onClick={() => {
                            sessionStorage.setItem(
                              "villaPage",
                              currentPage_villa,
                            );
                            navigate("/EditPropertyDetailElementor", {
                              state: { propertyid: villa.order },
                            });
                          }}
                        >
                          <RiFileEditFill size={14} />
                        </IconButton>
                        <IconButton
                          tip="Quick brochure"
                          tone="purple"
                          onClick={() =>
                            window.open(
                              `/NewQuickBrochure/${villa.order}`,
                              "_blank",
                            )
                          }
                        >
                          <FaFileArrowDown size={14} />
                        </IconButton>
                        <IconButton
                          tip="Delete property"
                          tone="red"
                          onClick={() => handleDeleteProperty(villa.order)}
                        >
                          <MdDelete size={15} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </TableShell>
          )}

          {/* ───────────────────────── HOTELS ───────────────────────── */}
          {active === "Hotels" && (
            <TableShell
              footer={<Pagination {...hotelPagination} />}
              head={
                <>
                  <th className={`${ui.th} w-10`}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll_hotel}
                      checked={
                        selectedRows_hotel.length ===
                          current_property_hotel.length &&
                        current_property_hotel.length > 0
                      }
                    />
                  </th>
                  <th className={ui.th}>Hotel name</th>
                  <th className={ui.th}>Property owner</th>
                  <th className={ui.th}>Property code</th>
                  <th className={ui.th}>Location</th>
                  <th className={ui.th}>Rating</th>
                  <th className={ui.th}>Availability</th>
                  <th className={ui.th}>Score</th>
                  <th className={ui.th}>Actions</th>
                </>
              }
            >
              {showLoader ? (
                <tr>
                  <td colSpan={8}>
                    <Loader
                      label="Loading..."
                      fullScreen={false}
                      className="py-6"
                    />
                  </td>
                </tr>
              ) : current_property_hotel.length === 0 ? (
                <EmptyRow colSpan={8}>No properties in this category</EmptyRow>
              ) : (
                current_property_hotel.map((hotel) => (
                  <tr key={hotel.order} className={rowCls}>
                    <td className={ui.td}>
                      <input
                        type="checkbox"
                        onChange={() =>
                          handleRowCheckboxChange_hotel(hotel.order)
                        }
                        checked={selectedRows_hotel.includes(hotel.order)}
                      />
                    </td>

                    <td className={ui.td} data-full-content={hotel.customer}>
                      <RankPopover
                        name={hotel.customer}
                        topValue={hotel.topFiveValue}
                        typeValue={hotel.topPropertyValue}
                        typeLabel="Hotel Rank"
                        availableTop={availableRanks}
                        availableType={availablePropertyRanks}
                        onSelectTop={(rank) =>
                          handleSetRank(hotel.order, "hotel", "topfive", rank)
                        }
                        onSelectType={(rank) =>
                          handleSetRank(hotel.order, "hotel", "property", rank)
                        }
                      />
                    </td>

                    <td className={ui.td}>
                      <Tooltip
                        title={hotel.propertyowner || "N/A"}
                        placement="left"
                      >
                        <span className="block max-w-[180px] truncate">
                          {hotel.propertyowner || "N/A"}
                        </span>
                      </Tooltip>
                    </td>
                    <td className={ui.td}>
                      <Tooltip
                        title={hotel.propertycode || "N/A"}
                        placement="left"
                      >
                        <span className="block max-w-[180px] truncate">
                          {hotel.propertycode || "N/A"}
                        </span>
                      </Tooltip>
                    </td>

                    <td className={ui.td}>
                      <Tooltip title={hotel.location || "N/A"} placement="left">
                        <span className="block max-w-[200px] truncate">
                          {hotel.location || "N/A"}
                        </span>
                      </Tooltip>
                    </td>

                    <td className={ui.td}>
                      <StarRating
                        rating={hotel.rating}
                        onRate={(val) =>
                          confirmRatingChange(
                            hotel.order,
                            "hotel",
                            val,
                            hotel.customer,
                          )
                        }
                      />
                    </td>

                    <td className={ui.td}>
                      <AvailabilityPill
                        item={hotel}
                        onToggle={handleAvailabilityToggle}
                      />
                    </td>

                    <td className={ui.td}>
                      <ProgressBar value={hotelProgress[hotel.order] ?? 0} />
                    </td>

                    <td className={ui.td}>
                      <div className="flex items-center gap-2">
                        <IconButton
                          tip="View hotel"
                          onClick={() =>
                            navigate("/EditPropertyDetailElementor", {
                              state: { propertyid: hotel.order },
                            })
                          }
                        >
                          <FaEye size={14} />
                        </IconButton>
                        <IconButton
                          tip="Edit hotel"
                          tone="yellow"
                          onClick={() => {
                            if (active === "Hotels") {
                              navigate("/EditHotelElementor", {
                                state: { propertyid: hotel.order },
                              });
                            } else {
                              navigate("/EditPropertyDetailElementor", {
                                state: { propertyid: hotel.order },
                              });
                            }
                          }}
                        >
                          <FaEdit size={14} />
                        </IconButton>
                        <IconButton
                          tip="Delete hotel"
                          tone="red"
                          onClick={() => handleDeleteProperty(hotel.order)}
                        >
                          <MdDelete size={15} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </TableShell>
          )}
        </div>
      </main>
    </div>
  );
};

export default Properties;
//--------------working codeeeeeee---------

// import { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import Menubar from "../Menubar/Menubar";
// import { Outlet, useNavigate } from "react-router-dom";
// import "../Dashboard/Dashboard.css";
// import { Tooltip } from "antd";
// import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
// import { MdKeyboardDoubleArrowRight } from "react-icons/md";
// import { FaEdit, FaEye } from "react-icons/fa";
// import { MdCancel } from "react-icons/md";
// import { MdDelete } from "react-icons/md";
// import { confirmAlert } from "react-confirm-alert";
// import { toast } from "react-toastify";
// import { FaTrash } from "react-icons/fa";
// import { RiFileEditFill } from "react-icons/ri";
// import { FaFileArrowDown } from "react-icons/fa6";
// import { MdHolidayVillage } from "react-icons/md";
// import { FaHotel } from "react-icons/fa6";
// import { VscPercentage } from "react-icons/vsc";
// import { FaWandMagicSparkles } from "react-icons/fa6";
// import { FaStar } from "react-icons/fa";
// import Loader from "../Loader/Loader";
// import useMinimumLoading from "../hooks/Useminimumloading";
// import { API_URL } from "../../Config";
// import { MdKeyboardArrowDown } from "react-icons/md";

// const MAX_RANK = 10;
// const ALL_RANKS = Array.from({ length: MAX_RANK }, (_, i) => i + 1);

// // const RankDropdown = ({ value, availableRanks = [], onSelect }) => {
// //   const [open, setOpen] = useState(false);
// //   const [pos, setPos] = useState({});
// //   const btnRef = useRef(null);
// //   const menuRef = useRef(null);
// //   const current = Number(value) || 0;

// //   useEffect(() => {
// //     if (!open) return;
// //     const inside = (e) =>
// //       btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target);
// //     const onDown = (e) => !inside(e) && setOpen(false);
// //     const onScroll = (e) => !inside(e) && setOpen(false);
// //     document.addEventListener("mousedown", onDown);
// //     window.addEventListener("scroll", onScroll, true);
// //     window.addEventListener("resize", () => setOpen(false));
// //     return () => {
// //       document.removeEventListener("mousedown", onDown);
// //       window.removeEventListener("scroll", onScroll, true);
// //     };
// //   }, [open]);

// //   const toggle = () => {
// //     if (!open) {
// //       const r = btnRef.current.getBoundingClientRect();
// //       const openUp = window.innerHeight - r.bottom < 380;
// //       setPos(
// //         openUp
// //           ? { bottom: window.innerHeight - r.top + 4, left: r.left }
// //           : { top: r.bottom + 4, left: r.left },
// //       );
// //     }
// //     setOpen((o) => !o);
// //   };

// //   const pick = (rank) => {
// //     setOpen(false);
// //     if (rank !== current) onSelect(rank);
// //   };

// //   return (
// //     <>
// //       <button
// //         ref={btnRef}
// //         type="button"
// //         onClick={toggle}
// //         className={`inline-flex items-center gap-1 h-7 pl-3 pr-2 rounded-full text-xs font-medium transition-colors ${
// //           current
// //             ? "bg-green-500 text-white hover:bg-green-600"
// //             : "bg-gray-200 text-gray-500 hover:bg-gray-300"
// //         }`}
// //       >
// //         {current ? `Rank: ${current}` : "Not set"}
// //         <MdKeyboardArrowDown
// //           size={16}
// //           className={`transition-transform ${open ? "rotate-180" : ""}`}
// //         />
// //       </button>

// //       {open && (
// //         <div
// //           ref={menuRef}
// //           style={{ position: "fixed", ...pos }}
// //           className="z-[100] w-32 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-xs"
// //         >
// //           {ALL_RANKS.map((r) => {
// //             const isCurrent = r === current;
// //             const taken = !isCurrent && !availableRanks.includes(r);
// //             return (
// //               <button
// //                 key={r}
// //                 type="button"
// //                 disabled={taken}
// //                 onClick={() => pick(r)}
// //                 className={`w-full flex items-center justify-between px-3 py-1.5 text-left ${
// //                   isCurrent
// //                     ? "bg-green-50 text-green-600 font-semibold"
// //                     : taken
// //                       ? "text-gray-300 cursor-not-allowed"
// //                       : "text-gray-700 hover:bg-gray-100"
// //                 }`}
// //               >
// //                 <span>Rank {r}</span>
// //                 {isCurrent && <span>✓</span>}
// //                 {taken && <span className="text-[10px]">Taken</span>}
// //               </button>
// //             );
// //           })}
// //           {current > 0 && (
// //             <button
// //               type="button"
// //               onClick={() => pick(0)}
// //               className="w-full text-left px-3 py-1.5 mt-1 border-t text-red-500 hover:bg-red-50"
// //             >
// //               Remove rank
// //             </button>
// //           )}
// //         </div>
// //       )}
// //     </>
// //   );
// // };
// const RankChips = ({ title, value, availableRanks, onSelect }) => {
//   const current = Number(value) || 0;
//   return (
//     <div>
//       <div className="flex items-center justify-between mb-1.5">
//         <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
//           {title}
//         </span>
//         {current > 0 && (
//           <button
//             type="button"
//             onClick={() => onSelect(0)}
//             className="text-[11px] text-red-500 hover:underline"
//           >
//             Remove
//           </button>
//         )}
//       </div>
//       <div className="grid grid-cols-5 gap-1">
//         {ALL_RANKS.map((r) => {
//           const isCurrent = r === current;
//           const taken = !isCurrent && !availableRanks.includes(r);
//           return (
//             <button
//               key={r}
//               type="button"
//               disabled={taken}
//               title={taken ? "Already taken" : `Set rank ${r}`}
//               onClick={() => !isCurrent && onSelect(r)}
//               className={`h-8 rounded-md text-xs font-medium border transition-colors ${
//                 isCurrent
//                   ? "bg-green-500 border-green-500 text-white"
//                   : taken
//                     ? "bg-gray-50 border-gray-100 text-gray-300 line-through cursor-not-allowed"
//                     : "bg-white border-gray-200 text-gray-700 hover:border-green-500 hover:text-green-600"
//               }`}
//             >
//               {r}
//             </button>
//           );
//         })}
//       </div>
//     </div>
//   );
// };

// const RankPopover = ({
//   name,
//   topValue,
//   typeValue,
//   typeLabel,
//   availableTop,
//   availableType,
//   onSelectTop,
//   onSelectType,
// }) => {
//   const [open, setOpen] = useState(false);
//   const [pos, setPos] = useState({});
//   const btnRef = useRef(null);
//   const menuRef = useRef(null);
//   const top = Number(topValue) || 0;
//   const typeRank = Number(typeValue) || 0;
//   const hasRank = top > 0 || typeRank > 0;

//   useEffect(() => {
//     if (!open) return;
//     const close = () => setOpen(false);
//     const outside = (e) =>
//       !btnRef.current?.contains(e.target) &&
//       !menuRef.current?.contains(e.target) &&
//       close();
//     const onScroll = (e) => !menuRef.current?.contains(e.target) && close();
//     document.addEventListener("mousedown", outside);
//     window.addEventListener("scroll", onScroll, true);
//     window.addEventListener("resize", close);
//     return () => {
//       document.removeEventListener("mousedown", outside);
//       window.removeEventListener("scroll", onScroll, true);
//       window.removeEventListener("resize", close);
//     };
//   }, [open]);

//   const toggle = () => {
//     if (!open) {
//       const r = btnRef.current.getBoundingClientRect();
//       const openUp = window.innerHeight - r.bottom < 300;
//       setPos(
//         openUp
//           ? { bottom: window.innerHeight - r.top + 4, left: r.left }
//           : { top: r.bottom + 4, left: r.left },
//       );
//     }
//     setOpen((o) => !o);
//   };

//   return (
//     <>
//       <div className="flex items-center gap-2">
//         <Tooltip title={name} placement="left">
//           <span>{name}</span>
//         </Tooltip>

//         {top > 0 && (
//           <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-600 text-[10px] font-semibold whitespace-nowrap">
//             Top {top}
//           </span>
//         )}
//         {typeRank > 0 && (
//           <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-600 text-[10px] font-semibold whitespace-nowrap">
//             #{typeRank}
//           </span>
//         )}

//         <button
//           ref={btnRef}
//           type="button"
//           onClick={toggle}
//           title="Set rankings"
//           className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
//             hasRank
//               ? "bg-green-500 text-white hover:bg-green-600"
//               : "bg-gray-200 text-gray-500 hover:bg-gray-300"
//           }`}
//         >
//           <MdKeyboardArrowDown
//             size={16}
//             className={`transition-transform ${open ? "rotate-180" : ""}`}
//           />
//         </button>
//       </div>

//       {open && (
//         <div
//           ref={menuRef}
//           style={{ position: "fixed", ...pos }}
//           className="z-[100] w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 space-y-3"
//         >
//           <RankChips
//             title="Top 10 Property"
//             value={topValue}
//             availableRanks={availableTop}
//             onSelect={onSelectTop}
//           />
//           <div className="border-t border-gray-100" />
//           <RankChips
//             title={typeLabel}
//             value={typeValue}
//             availableRanks={availableType}
//             onSelect={onSelectType}
//           />
//         </div>
//       )}
//     </>
//   );
// };
// const ProgressBar = ({ value }) => {
//   const getColor = () => {
//     if (value <= 50) return "bg-red-500";
//     if (value < 100) return "bg-yellow-300";
//     return "bg-green-500";
//   };
//   const getTextColor = () => {
//     if (value <= 50) return "text-red-500";
//     if (value < 100) return "text-yellow-400";
//     return "text-green-500";
//   };

//   return (
//     <div className="flex flex-col items-center">
//       {/* percentage text */}
//       <div className={`text-sm font-semibold mb-1 ${getTextColor()}`}>
//         {value}%
//       </div>

//       {/* smaller progress bar */}
//       <div className="w-20 bg-gray-200 h-2 rounded">
//         <div
//           className={`h-2 rounded ${getColor()} transition-all duration-500`}
//           style={{ width: `${value}%` }}
//         />
//       </div>
//     </div>
//   );
// };
// const StarRating = ({ rating = 0, onRate }) => {
//   const [hover, setHover] = useState(0);

//   return (
//     <div className="flex gap-0.5">
//       {[1, 2, 3, 4, 5].map((star) => (
//         <button
//           key={star}
//           type="button"
//           onClick={() => onRate(star)}
//           onMouseEnter={() => setHover(star)}
//           onMouseLeave={() => setHover(0)}
//           className="focus:outline-none transition-transform hover:scale-110"
//         >
//           <FaStar
//             size={16}
//             className={
//               (hover || rating) >= star ? "text-yellow-400" : "text-gray-300"
//             }
//           />
//         </button>
//       ))}
//     </div>
//   );
// };
// const Properties = () => {
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
//   // const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
//     const savedState = localStorage.getItem("menubarOpen");

//     if (savedState !== null) {
//       return JSON.parse(savedState);
//     }

//     return window.innerWidth >= 1024;
//   });
//   const [searchQuery, setSearchQuery] = useState("");
//   const [isLoading, setIsLoading] = useState(true);
//   const showLoader = useMinimumLoading(isLoading);
//   const [table_property_villa, setTableProperty_villa] = useState([]);
//   const [table_property_hotel, setTableProperty_hotel] = useState([]);
//   const [table_property_resort, setTableProperty_resort] = useState([]);
//   const [table_property_apartment, setTableProperty_apartment] = useState([]);
//   const [villaProgress, setVillaProgress] = useState({});
//   const [hotelProgress, setHotelProgress] = useState({});
//   // const value = villa.progress;
//   // const value = 50;
//   // Villa pagination
//   // const rowsPerPage_villa = 20;
//   // const [currentPage_villa, setCurrentPage_villa] = useState(1);
//   // const filtered_property_villa = table_property_villa.filter((villa) =>
//   //   [villa.customer, villa.propertyowner, villa.location].some((field) =>
//   //     field?.toLowerCase().includes(searchQuery.toLowerCase())
//   //   )
//   // );
//   // const indexOfLastTable_villa = currentPage_villa * rowsPerPage_villa;
//   // const indexOfFirstTable_villa = indexOfLastTable_villa - rowsPerPage_villa;

//   // const current_property_villa = filtered_property_villa.slice(
//   //   indexOfFirstTable_villa,
//   //   indexOfLastTable_villa
//   // );

//   // const totalPages_villa = Math.ceil(
//   //   filtered_property_villa.length / rowsPerPage_villa
//   // );

//   // const handlePageChange_villa = (pageNumber) => {
//   //   setCurrentPage_villa(pageNumber);
//   // };

//   // const handleNextPage_villa = () => {
//   //   if (currentPage_villa < totalPages_villa) {
//   //     setCurrentPage_villa(currentPage_villa + 1);
//   //   }
//   // };

//   // const handlePrevPage_villa = () => {
//   //   if (currentPage_villa > 1) {
//   //     setCurrentPage_villa(currentPage_villa - 1);
//   //   }
//   // };

//   // const generatePageNumbers_villa = () => {
//   //   const pages = [];
//   //   if (totalPages_villa <= 3) {
//   //     for (let i = 1; i <= totalPages_villa; i++) pages.push(i);
//   //   } else {
//   //     if (currentPage_villa > 2) {
//   //       pages.push(1, "...");
//   //     }
//   //     for (let i = currentPage_villa - 1; i <= currentPage_villa + 1; i++) {
//   //       if (i > 0 && i <= totalPages_villa) pages.push(i);
//   //     }
//   //     if (currentPage_villa < totalPages_villa - 1) {
//   //       pages.push("...", totalPages_villa);
//   //     }
//   //   }
//   //   return pages;
//   // };
//   // Villa pagination
//   const rowsPerPage_villa = 20;
//   // const [currentPage_villa, setCurrentPage_villa] = useState(1);
//   const [currentPage_villa, setCurrentPage_villa] = useState(() => {
//     const saved = sessionStorage.getItem("villaPage");
//     return saved ? parseInt(saved, 10) : 1;
//   });

//   // Filter properties based on search query
//   // const filtered_property_villa = table_property_villa.filter((villa) =>
//   //   [villa.customer, villa.propertyowner, villa.location].some((field) =>
//   //     field?.toLowerCase().includes(searchQuery.toLowerCase()),
//   //   ),
//   // );
//   const filtered_property_villa = table_property_villa.filter((villa) => {
//     const query = searchQuery.toLowerCase();

//     const progress = villaProgress[villa.order]?.toString() || "";

//     return [villa.customer, villa.propertyowner, villa.location, progress].some(
//       (field) => field?.toLowerCase().includes(query),
//     );
//   });
//   // Apply custom sorting
//   const sorted_property_villa = [...filtered_property_villa].sort((a, b) => {
//     const aIsToggled = a.topPropertyEnabled || a.topFiveEnabled;
//     const bIsToggled = b.topPropertyEnabled || b.topFiveEnabled;

//     if (aIsToggled === bIsToggled) {
//       return b.order - a.order;
//     }

//     return aIsToggled && !bIsToggled ? -1 : 1;
//   });

//   // Apply pagination
//   const indexOfLastTable_villa = currentPage_villa * rowsPerPage_villa;
//   const indexOfFirstTable_villa = indexOfLastTable_villa - rowsPerPage_villa;

//   const current_property_villa = sorted_property_villa.slice(
//     indexOfFirstTable_villa,
//     indexOfLastTable_villa,
//   );

//   const totalPages_villa = Math.ceil(
//     sorted_property_villa.length / rowsPerPage_villa,
//   );

//   // The rest of your pagination functions remain unchanged
//   const handlePageChange_villa = (pageNumber) => {
//     setCurrentPage_villa(pageNumber);
//   };

//   const handleNextPage_villa = () => {
//     if (currentPage_villa < totalPages_villa) {
//       setCurrentPage_villa(currentPage_villa + 1);
//     }
//   };

//   const handlePrevPage_villa = () => {
//     if (currentPage_villa > 1) {
//       setCurrentPage_villa(currentPage_villa - 1);
//     }
//   };

//   const generatePageNumbers_villa = () => {
//     const pages = [];
//     if (totalPages_villa <= 3) {
//       for (let i = 1; i <= totalPages_villa; i++) pages.push(i);
//     } else {
//       if (currentPage_villa > 2) {
//         pages.push(1, "...");
//       }
//       for (let i = currentPage_villa - 1; i <= currentPage_villa + 1; i++) {
//         if (i > 0 && i <= totalPages_villa) pages.push(i);
//       }
//       if (currentPage_villa < totalPages_villa - 1) {
//         pages.push("...", totalPages_villa);
//       }
//     }
//     return pages;
//   };

//   // ---------------- Hotels ----------------
//   // const rowsPerPage_hotel = 20;
//   // const [currentPage_hotel, setCurrentPage_hotel] = useState(1);

//   // const filtered_property_hotel = table_property_hotel.filter((hotel) =>
//   //   [hotel.customer, hotel.propertyowner, hotel.location].some((field) =>
//   //     field?.toLowerCase().includes(searchQuery.toLowerCase())
//   //   )
//   // );
//   // const indexOfLastTable_hotel = currentPage_hotel * rowsPerPage_hotel;
//   // const indexOfFirstTable_hotel = indexOfLastTable_hotel - rowsPerPage_hotel;
//   // const current_property_hotel = filtered_property_hotel.slice(
//   //   indexOfFirstTable_hotel,
//   //   indexOfLastTable_hotel
//   // );
//   // const totalPages_hotel = Math.ceil(
//   //   filtered_property_hotel.length / rowsPerPage_hotel
//   // );

//   // const handlePageChange_hotel = (pageNumber) =>
//   //   setCurrentPage_hotel(pageNumber);
//   // const handleNextPage_hotel = () => {
//   //   if (currentPage_hotel < totalPages_hotel)
//   //     setCurrentPage_hotel((p) => p + 1);
//   // };
//   // const handlePrevPage_hotel = () => {
//   //   if (currentPage_hotel > 1) setCurrentPage_hotel((p) => p - 1);
//   // };
//   // const generatePageNumbers_hotel = () => {
//   //   const pageNumbers = [];
//   //   if (totalPages_hotel <= 3) {
//   //     for (let i = 1; i <= totalPages_hotel; i++) pageNumbers.push(i);
//   //   } else {
//   //     if (currentPage_hotel > 2) {
//   //       pageNumbers.push(1);
//   //       pageNumbers.push("...");
//   //     }
//   //     for (let i = currentPage_hotel - 1; i <= currentPage_hotel + 1; i++) {
//   //       if (i > 0 && i <= totalPages_hotel) pageNumbers.push(i);
//   //     }
//   //     if (currentPage_hotel < totalPages_hotel - 1) {
//   //       pageNumbers.push("...");
//   //       pageNumbers.push(totalPages_hotel);
//   //     }
//   //   }
//   //   return pageNumbers;
//   // };
//   const rowsPerPage_hotel = 20;
//   const [currentPage_hotel, setCurrentPage_hotel] = useState(1);
//   const filtered_property_hotel = table_property_hotel.filter((hotel) => {
//     const query = searchQuery.toLowerCase();

//     const progress = hotelProgress[hotel.order]?.toString() || "";

//     return [hotel.customer, hotel.propertyowner, hotel.location, progress].some(
//       (field) => field?.toLowerCase().includes(query),
//     );
//   });
//   // const filtered_property_hotel = table_property_hotel.filter((hotel) =>
//   //   [hotel.customer, hotel.propertyowner, hotel.location].some((field) =>
//   //     field?.toLowerCase().includes(searchQuery.toLowerCase()),
//   //   ),
//   // );

//   // Apply custom sorting for hotels
//   const sorted_property_hotel = [...filtered_property_hotel].sort((a, b) => {
//     const aIsToggled = a.topPropertyEnabled || a.topFiveEnabled;
//     const bIsToggled = b.topPropertyEnabled || b.topFiveEnabled;

//     if (aIsToggled === bIsToggled) {
//       return b.order - a.order;
//     }

//     return aIsToggled && !bIsToggled ? -1 : 1;
//   });

//   const indexOfLastTable_hotel = currentPage_hotel * rowsPerPage_hotel;
//   const indexOfFirstTable_hotel = indexOfLastTable_hotel - rowsPerPage_hotel;
//   const current_property_hotel = sorted_property_hotel.slice(
//     indexOfFirstTable_hotel,
//     indexOfLastTable_hotel,
//   );
//   const totalPages_hotel = Math.ceil(
//     sorted_property_hotel.length / rowsPerPage_hotel,
//   );

//   const handlePageChange_hotel = (pageNumber) =>
//     setCurrentPage_hotel(pageNumber);
//   const handleNextPage_hotel = () => {
//     if (currentPage_hotel < totalPages_hotel)
//       setCurrentPage_hotel((p) => p + 1);
//   };
//   const handlePrevPage_hotel = () => {
//     if (currentPage_hotel > 1) setCurrentPage_hotel((p) => p - 1);
//   };
//   const generatePageNumbers_hotel = () => {
//     const pageNumbers = [];
//     if (totalPages_hotel <= 3) {
//       for (let i = 1; i <= totalPages_hotel; i++) pageNumbers.push(i);
//     } else {
//       if (currentPage_hotel > 2) {
//         pageNumbers.push(1);
//         pageNumbers.push("...");
//       }
//       for (let i = currentPage_hotel - 1; i <= currentPage_hotel + 1; i++) {
//         if (i > 0 && i <= totalPages_hotel) pageNumbers.push(i);
//       }
//       if (currentPage_hotel < totalPages_hotel - 1) {
//         pageNumbers.push("...");
//         pageNumbers.push(totalPages_hotel);
//       }
//     }
//     return pageNumbers;
//   };

//   // ---------------- Resorts ----------------
//   // const rowsPerPage_resort = 20;
//   // const [currentPage_resort, setCurrentPage_resort] = useState(1);

//   // const filtered_property_resort = table_property_resort.filter((resort) =>
//   //   [resort.customer, resort.propertyowner, resort.location].some((field) =>
//   //     field?.toLowerCase().includes(searchQuery.toLowerCase())
//   //   )
//   // );
//   // const indexOfLastTable_resort = currentPage_resort * rowsPerPage_resort;
//   // const indexOfFirstTable_resort = indexOfLastTable_resort - rowsPerPage_resort;
//   // const current_property_resort = filtered_property_resort.slice(
//   //   indexOfFirstTable_resort,
//   //   indexOfLastTable_resort
//   // );
//   // const totalPages_resort = Math.ceil(
//   //   filtered_property_resort.length / rowsPerPage_resort
//   // );

//   // const handlePageChange_resort = (pageNumber) =>
//   //   setCurrentPage_resort(pageNumber);
//   // const handleNextPage_resort = () => {
//   //   if (currentPage_resort < totalPages_resort)
//   //     setCurrentPage_resort((p) => p + 1);
//   // };
//   // const handlePrevPage_resort = () => {
//   //   if (currentPage_resort > 1) setCurrentPage_resort((p) => p - 1);
//   // };
//   // const generatePageNumbers_resort = () => {
//   //   const pageNumbers = [];
//   //   if (totalPages_resort <= 3) {
//   //     for (let i = 1; i <= totalPages_resort; i++) pageNumbers.push(i);
//   //   } else {
//   //     if (currentPage_resort > 2) {
//   //       pageNumbers.push(1);
//   //       pageNumbers.push("...");
//   //     }
//   //     for (let i = currentPage_resort - 1; i <= currentPage_resort + 1; i++) {
//   //       if (i > 0 && i <= totalPages_resort) pageNumbers.push(i);
//   //     }
//   //     if (currentPage_resort < totalPages_resort - 1) {
//   //       pageNumbers.push("...");
//   //       pageNumbers.push(totalPages_resort);
//   //     }
//   //   }
//   //   return pageNumbers;
//   // };
//   const rowsPerPage_resort = 20;
//   const [currentPage_resort, setCurrentPage_resort] = useState(1);

//   const filtered_property_resort = table_property_resort.filter((resort) =>
//     [resort.customer, resort.propertyowner, resort.location].some((field) =>
//       field?.toLowerCase().includes(searchQuery.toLowerCase()),
//     ),
//   );

//   // Apply custom sorting for resorts
//   const sorted_property_resort = [...filtered_property_resort].sort((a, b) => {
//     const aIsToggled = a.topPropertyEnabled || a.topFiveEnabled;
//     const bIsToggled = b.topPropertyEnabled || b.topFiveEnabled;

//     if (aIsToggled === bIsToggled) {
//       return b.order - a.order;
//     }

//     return aIsToggled && !bIsToggled ? -1 : 1;
//   });

//   const indexOfLastTable_resort = currentPage_resort * rowsPerPage_resort;
//   const indexOfFirstTable_resort = indexOfLastTable_resort - rowsPerPage_resort;
//   const current_property_resort = sorted_property_resort.slice(
//     indexOfFirstTable_resort,
//     indexOfLastTable_resort,
//   );
//   const totalPages_resort = Math.ceil(
//     sorted_property_resort.length / rowsPerPage_resort,
//   );

//   const handlePageChange_resort = (pageNumber) =>
//     setCurrentPage_resort(pageNumber);
//   const handleNextPage_resort = () => {
//     if (currentPage_resort < totalPages_resort)
//       setCurrentPage_resort((p) => p + 1);
//   };
//   const handlePrevPage_resort = () => {
//     if (currentPage_resort > 1) setCurrentPage_resort((p) => p - 1);
//   };
//   const generatePageNumbers_resort = () => {
//     const pageNumbers = [];
//     if (totalPages_resort <= 3) {
//       for (let i = 1; i <= totalPages_resort; i++) pageNumbers.push(i);
//     } else {
//       if (currentPage_resort > 2) {
//         pageNumbers.push(1);
//         pageNumbers.push("...");
//       }
//       for (let i = currentPage_resort - 1; i <= currentPage_resort + 1; i++) {
//         if (i > 0 && i <= totalPages_resort) pageNumbers.push(i);
//       }
//       if (currentPage_resort < totalPages_resort - 1) {
//         pageNumbers.push("...");
//         pageNumbers.push(totalPages_resort);
//       }
//     }
//     return pageNumbers;
//   };
//   // ---------------- Apartments ----------------
//   // const rowsPerPage_apartment = 20;
//   // const [currentPage_apartment, setCurrentPage_apartment] = useState(1);

//   // const filtered_property_apartment = table_property_apartment.filter(
//   //   (apartment) =>
//   //     [apartment.customer, apartment.propertyowner, apartment.location].some(
//   //       (field) => field?.toLowerCase().includes(searchQuery.toLowerCase())
//   //     )
//   // );
//   // const indexOfLastTable_apartment =
//   //   currentPage_apartment * rowsPerPage_apartment;
//   // const indexOfFirstTable_apartment =
//   //   indexOfLastTable_apartment - rowsPerPage_apartment;
//   // const current_property_apartment = filtered_property_apartment.slice(
//   //   indexOfFirstTable_apartment,
//   //   indexOfLastTable_apartment
//   // );
//   // const totalPages_apartment = Math.ceil(
//   //   filtered_property_apartment.length / rowsPerPage_apartment
//   // );

//   // const handlePageChange_apartment = (pageNumber) =>
//   //   setCurrentPage_apartment(pageNumber);
//   // const handleNextPage_apartment = () => {
//   //   if (currentPage_apartment < totalPages_apartment)
//   //     setCurrentPage_apartment((p) => p + 1);
//   // };
//   // const handlePrevPage_apartment = () => {
//   //   if (currentPage_apartment > 1) setCurrentPage_apartment((p) => p - 1);
//   // };
//   // const generatePageNumbers_apartment = () => {
//   //   const pageNumbers = [];
//   //   if (totalPages_apartment <= 3) {
//   //     for (let i = 1; i <= totalPages_apartment; i++) pageNumbers.push(i);
//   //   } else {
//   //     if (currentPage_apartment > 2) {
//   //       pageNumbers.push(1);
//   //       pageNumbers.push("...");
//   //     }
//   //     for (
//   //       let i = currentPage_apartment - 1;
//   //       i <= currentPage_apartment + 1;
//   //       i++
//   //     ) {
//   //       if (i > 0 && i <= totalPages_apartment) pageNumbers.push(i);
//   //     }
//   //     if (currentPage_apartment < totalPages_apartment - 1) {
//   //       pageNumbers.push("...");
//   //       pageNumbers.push(totalPages_apartment);
//   //     }
//   //   }
//   //   return pageNumbers;
//   // };
//   const rowsPerPage_apartment = 20;
//   const [currentPage_apartment, setCurrentPage_apartment] = useState(1);

//   const filtered_property_apartment = table_property_apartment.filter(
//     (apartment) =>
//       [apartment.customer, apartment.propertyowner, apartment.location].some(
//         (field) => field?.toLowerCase().includes(searchQuery.toLowerCase()),
//       ),
//   );

//   // Apply custom sorting for apartments
//   const sorted_property_apartment = [...filtered_property_apartment].sort(
//     (a, b) => {
//       const aIsToggled = a.topPropertyEnabled || a.topFiveEnabled;
//       const bIsToggled = b.topPropertyEnabled || b.topFiveEnabled;

//       if (aIsToggled === bIsToggled) {
//         return b.order - a.order;
//       }

//       return aIsToggled && !bIsToggled ? -1 : 1;
//     },
//   );

//   const indexOfLastTable_apartment =
//     currentPage_apartment * rowsPerPage_apartment;
//   const indexOfFirstTable_apartment =
//     indexOfLastTable_apartment - rowsPerPage_apartment;
//   const current_property_apartment = sorted_property_apartment.slice(
//     indexOfFirstTable_apartment,
//     indexOfLastTable_apartment,
//   );
//   const totalPages_apartment = Math.ceil(
//     sorted_property_apartment.length / rowsPerPage_apartment,
//   );

//   const handlePageChange_apartment = (pageNumber) =>
//     setCurrentPage_apartment(pageNumber);
//   const handleNextPage_apartment = () => {
//     if (currentPage_apartment < totalPages_apartment)
//       setCurrentPage_apartment((p) => p + 1);
//   };
//   const handlePrevPage_apartment = () => {
//     if (currentPage_apartment > 1) setCurrentPage_apartment((p) => p - 1);
//   };
//   const generatePageNumbers_apartment = () => {
//     const pageNumbers = [];
//     if (totalPages_apartment <= 3) {
//       for (let i = 1; i <= totalPages_apartment; i++) pageNumbers.push(i);
//     } else {
//       if (currentPage_apartment > 2) {
//         pageNumbers.push(1);
//         pageNumbers.push("...");
//       }
//       for (
//         let i = currentPage_apartment - 1;
//         i <= currentPage_apartment + 1;
//         i++
//       ) {
//         if (i > 0 && i <= totalPages_apartment) pageNumbers.push(i);
//       }
//       if (currentPage_apartment < totalPages_apartment - 1) {
//         pageNumbers.push("...");
//         pageNumbers.push(totalPages_apartment);
//       }
//     }
//     return pageNumbers;
//   };
//   useEffect(() => {
//     const fetchAllProgress = async () => {
//       try {
//         const res = await fetch(`${API_URL}/property-completion-all`);

//         const data = await res.json();

//         // data format: {1: 72.6, 2: 80.4, 3: 69.4}

//         setVillaProgress(data);
//         setHotelProgress(data);
//       } catch (err) {
//         console.error("Failed to fetch progress:", err);
//       }
//     };

//     fetchAllProgress();
//   }, []);
//   // useEffect(() => {
//   //   if (!current_property_villa || current_property_villa.length === 0) return;

//   //   const fetchAllProgress = async () => {
//   //     const results = {};
//   //     await Promise.all(
//   //       current_property_villa.map(async (villa) => {
//   //         try {
//   //           const res = await fetch(
//   //             `https://dash.zorbastays.com/web-backend/property-completion/${villa.order}`, // ✅ was villa.id
//   //           );
//   //           const data = await res.json();
//   //           results[villa.order] = data.completionPct ?? 0; // ✅ was villa.id
//   //         } catch {
//   //           results[villa.order] = 0; // ✅ was villa.id
//   //         }
//   //       }),
//   //     );
//   //     setVillaProgress(results);
//   //   };

//   //   fetchAllProgress();
//   // }, [current_property_villa]);
//   // useEffect(() => {
//   //   if (!current_property_hotel || current_property_hotel.length === 0) return;

//   //   const fetchAllProgress = async () => {
//   //     const results = {};
//   //     await Promise.all(
//   //       current_property_hotel.map(async (hotel) => {
//   //         try {
//   //           const res = await fetch(
//   //             `https://dash.zorbastays.com/web-backend/property-completion/${hotel.order}`,
//   //           );
//   //           const data = await res.json();
//   //           results[hotel.order] = data.completionPct ?? 0;
//   //         } catch {
//   //           results[hotel.order] = 0;
//   //         }
//   //       }),
//   //     );
//   //     setHotelProgress(results);
//   //   };

//   //   fetchAllProgress();
//   // }, [current_property_hotel]);

//   useEffect(() => {
//     const handleResize = () => {
//       const mobile = window.innerWidth < 1024;
//       setIsMobile(mobile);

//       if (mobile) {
//         setIsSidebarOpen(false);
//       }
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const navigate = useNavigate();

//   const statusColors = {
//     CANCELLED: "bg-amber-800/20 text-amber-800",
//     CONFIRMED: "bg-green-500/20 text-green-500",
//     BOOKED: "bg-purple-500/20 text-purple-500",
//     FAILED: "bg-red-500/20 text-red-500",
//     PENDING: "bg-yellow-200/40 text-yellow-300",
//     PAID: "bg-transparent text-green-500",
//     UNPAID: "bg-transparent text-red-400",
//   };

//   const [isOpen_popupForm, setIsOpen_popupForm] = useState(false);

//   const [, setShowPopup_filter] = useState(false); //showPopup_filter
//   const filterRef = useRef(null);

//   useEffect(() => {
//     function handleClickOutside(event) {
//       if (filterRef.current && !filterRef.current.contains(event.target)) {
//         setShowPopup_filter(false);
//       }
//     }

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const [active, setActive] = useState("Villas");

//   // const options = ["Villas", "Hotels", "Resorts", "Apartments"];
//   const options = ["Villas", "Hotels"];

//   const [selectedRows_apartment, setSelectedRows_apartment] = useState([]);

//   const [selectedRows_villa, setSelectedRows_villa] = useState([]);

//   const handleRowCheckboxChange_villa = (order) => {
//     setSelectedRows_villa((prevSelected) =>
//       prevSelected.includes(order)
//         ? prevSelected.filter((id) => id !== order)
//         : [...prevSelected, order],
//     );
//   };

//   const handleSelectAll_villa = () => {
//     if (selectedRows_villa.length === current_property_villa.length) {
//       setSelectedRows_villa([]);
//     } else {
//       setSelectedRows_villa(
//         current_property_villa.map((booking) => booking.order),
//       );
//     }
//   };
//   const handleSelectAll_apartment = (e) => {
//     if (e.target.checked) {
//       setSelectedRows_apartment(current_property_apartment.map((p) => p.id));
//     } else {
//       setSelectedRows_apartment([]);
//     }
//   };

//   const loadAvailableRanks = async () => {
//     try {
//       const [g, p] = await Promise.all([
//         axios.get(`${API_URL}/getTakenTopFive`),
//         axios.get(`${API_URL}/getTakenTopProperty`),
//       ]);
//       const free = (r) =>
//         ALL_RANKS.filter((x) => !(r.data.taken || []).includes(x));
//       if (g.data.success) setAvailableRanks(free(g));
//       if (p.data.success) setAvailablePropertyRanks(free(p));
//     } catch (err) {
//       console.error("Failed to load available ranks", err);
//     }
//   };

//   useEffect(() => {
//     loadAvailableRanks();
//   }, []);

//   // Refresh Resort ranks
//   const refreshResortRanks = async () => {
//     try {
//       const res = await axios.get(
//         `${API_URL}/getTakenTopProperty?propertytype=resort`,
//       );
//       if (res.data.success) {
//         const allRanks = [1, 2, 3, 4, 5];
//         const taken = res.data.taken || [];
//         const available = allRanks.filter((r) => !taken.includes(r));
//         setAvailableResortRanks(available);
//       }
//     } catch (err) {
//       console.error("Failed to refresh resort ranks", err);
//     }
//   };

//   const [selectedRows_hotel, setSelectedRows_hotel] = useState([]);

//   const handleRowCheckboxChange_hotel = (order) => {
//     setSelectedRows_hotel((prevSelected) =>
//       prevSelected.includes(order)
//         ? prevSelected.filter((id) => id !== order)
//         : [...prevSelected, order],
//     );
//   };

//   const handleSelectAll_hotel = () => {
//     if (selectedRows_hotel.length === current_property_hotel.length) {
//       setSelectedRows_hotel([]);
//     } else {
//       setSelectedRows_hotel(
//         current_property_hotel.map((booking) => booking.order),
//       );
//     }
//   };

//   const handleToggle_topProperty_hotel = async (order) => {
//     const hotel = table_property_hotel.find((item) => item.order === order);
//     const newEnabled = !hotel.topPropertyEnabled;

//     if (!newEnabled) {
//       // Turning OFF → clear in DB
//       try {
//         const res = await axios.post(`${API_URL}/update-rank`, {
//           propertyid: order,
//           propertytype: "hotel",
//           rank: 0,
//         });

//         if (res.data.success) {
//           const updated = table_property_hotel.map((item) =>
//             item.order === order
//               ? { ...item, topPropertyEnabled: false, topPropertyValue: "" }
//               : item,
//           );
//           setTableProperty_hotel(updated);
//           // await refreshHotelRanks();
//         } else {
//           alert(res.data.message);
//         }
//       } catch (err) {
//         console.error("Failed to reset top property (hotel)", err);
//         alert("Error updating top property toggle");
//       }
//     } else {
//       // Turning ON → just enable, wait for dropdown
//       const updated = table_property_hotel.map((item) =>
//         item.order === order
//           ? { ...item, topPropertyEnabled: true, topPropertyValue: "" }
//           : item,
//       );
//       setTableProperty_hotel(updated);
//     }
//   };

//   const handleDropdownChange_topProperty_hotel = async (order, value) => {
//     // const hotel = table_property_hotel.find((item) => item.order === order);

//     try {
//       const res = await axios.post(`${API_URL}/update-rank`, {
//         propertyid: order,
//         propertytype: "hotel",
//         rank: parseInt(value, 10), // ensure number
//       });

//       if (res.data.success) {
//         const updated = table_property_hotel.map((item) =>
//           item.order === order ? { ...item, topPropertyValue: value } : item,
//         );
//         setTableProperty_hotel(updated);
//         await fetchProperties(); // refresh Hotels table
//         // await refreshHotelRanks();
//       } else {
//         alert(res.data.message);
//       }
//     } catch (err) {
//       console.error("Failed to update hotel rank", err);
//       alert("Error updating hotel rank");
//     }
//   };

//   // const [table_property_resort, setTableProperty_resort] = useState([]);
//   const fetchProperties = async (type) => {
//     setIsLoading(true);
//     try {
//       const res = await axios.get(`${API_URL}/type/${type.toLowerCase()}`);
//       const data = res.data.map((item) => ({
//         order: item.id,
//         customer: item.propertyname,
//         productName: item.description,
//         location: item.location,
//         payment: item.currentprice,
//         status: "CONFIRMED",
//         topPropertyEnabled: item.istopproperty > 0,
//         topPropertyValue: item.istopproperty || "",
//         topFiveEnabled: item.istopfiverank > 0,
//         topFiveValue: item.istopfiverank || "",
//         propertyowner: item.propertyowner,
//         availability: item.availability,
//         rating: item.rating ?? null,
//       }));

//       if (type === "villa") setTableProperty_villa(data);
//       if (type === "hotel") setTableProperty_hotel(data);
//       if (type === "resort") setTableProperty_resort(data);
//       if (type === "apartment") setTableProperty_apartment(data);
//     } catch (err) {
//       console.error("Failed to fetch properties", err);
//     } finally {
//       setIsLoading(false);
//     }
//   };
//   const fetchVillaStoreProperties = async () => {
//     setIsLoading(true);

//     try {
//       const res = await axios.get(`${API_URL}/villa-store`);

//       const data = res.data.map((item) => ({
//         order: item.id,
//         customer: item.propertyname,
//         productName: item.description,
//         location: item.location,
//         payment: item.currentprice,
//         status: "CONFIRMED",

//         topPropertyEnabled: item.istopproperty > 0,
//         topPropertyValue: item.istopproperty || "",

//         topFiveEnabled: item.istopfiverank > 0,
//         topFiveValue: item.istopfiverank || "",

//         propertyowner: item.propertyowner,
//         availability: item.availability,
//         rating: item.rating ?? null,

//         // VERY IMPORTANT
//         propertytype: item.propertytype,
//       }));

//       setTableProperty_villa(data);
//     } catch (err) {
//       console.error("Failed to fetch Villa Store properties", err);
//     } finally {
//       setIsLoading(false);
//     }
//   };
//   const isFirstMount = useRef(true);

//   useEffect(() => {
//     if (isFirstMount.current) {
//       isFirstMount.current = false;
//       if (active === "Villas") fetchVillaStoreProperties();
//       if (active === "Hotels") fetchProperties("hotel");
//       if (active === "Resorts") fetchProperties("resort");
//       if (active === "Apartments") fetchProperties("apartment");
//       return;
//     }

//     if (active === "Villas") {
//       fetchVillaStoreProperties();
//     }
//     if (active === "Hotels") {
//       setCurrentPage_hotel(1);
//       fetchProperties("hotel");
//     }
//     if (active === "Resorts") {
//       setCurrentPage_resort(1);
//       fetchProperties("resort");
//     }
//     if (active === "Apartments") {
//       setCurrentPage_apartment(1);
//       fetchProperties("apartment");
//     }
//   }, [active]);

//   useEffect(() => {
//     if (searchQuery !== "") {
//       if (active === "Villas") setCurrentPage_villa(1);
//       if (active === "Hotels") setCurrentPage_hotel(1);
//       if (active === "Resorts") setCurrentPage_resort(1);
//       if (active === "Apartments") setCurrentPage_apartment(1);
//     }
//   }, [searchQuery]);
//   const [selectedRows_resort, setSelectedRows_resort] = useState([]);

//   const handleRowCheckboxChange_resort = (order) => {
//     setSelectedRows_resort((prevSelected) =>
//       prevSelected.includes(order)
//         ? prevSelected.filter((id) => id !== order)
//         : [...prevSelected, order],
//     );
//   };

//   const handleSelectAll_resort = () => {
//     if (selectedRows_resort.length === current_property_resort.length) {
//       setSelectedRows_resort([]);
//     } else {
//       setSelectedRows_resort(
//         current_property_resort.map((booking) => booking.order),
//       );
//     }
//   };

//   const handleToggle_topProperty_resort = async (order) => {
//     const resort = table_property_resort.find((item) => item.order === order);
//     const newEnabled = !resort.topPropertyEnabled;

//     if (!newEnabled) {
//       // Turning OFF → clear in DB
//       try {
//         const res = await axios.post(`${API_URL}/update-rank`, {
//           propertyid: order,
//           propertytype: "resort",
//           rank: 0,
//         });

//         if (res.data.success) {
//           const updated = table_property_resort.map((item) =>
//             item.order === order
//               ? { ...item, topPropertyEnabled: false, topPropertyValue: "" }
//               : item,
//           );
//           setTableProperty_resort(updated);
//           await refreshResortRanks();
//         } else {
//           alert(res.data.message);
//         }
//       } catch (err) {
//         console.error("Failed to reset top property (resort)", err);
//         alert("Error updating top property toggle");
//       }
//     } else {
//       // Turning ON → just enable, wait for dropdown
//       const updated = table_property_resort.map((item) =>
//         item.order === order
//           ? { ...item, topPropertyEnabled: true, topPropertyValue: "" }
//           : item,
//       );
//       setTableProperty_resort(updated);
//     }
//   };

//   const handleDropdownChange_topProperty_resort = async (order, value) => {
//     // const resort = table_property_resort.find((item) => item.order === order);

//     try {
//       const res = await axios.post(`${API_URL}/update-rank`, {
//         propertyid: order,
//         propertytype: "resort",
//         rank: parseInt(value, 10), // ensure number
//       });

//       if (res.data.success) {
//         const updated = table_property_resort.map((item) =>
//           item.order === order ? { ...item, topPropertyValue: value } : item,
//         );
//         setTableProperty_resort(updated);
//         await fetchProperties(); // refresh Resorts table
//         await refreshResortRanks();
//       } else {
//         alert(res.data.message);
//       }
//     } catch (err) {
//       console.error("Failed to update resort rank", err);
//       alert("Error updating resort rank");
//     }
//   };

//   const [isOpen_dropdownExp, setIsOpen_dropdownExp] = useState(false);
//   const [selectedOptions_dropdownExp, setSelectedOptions_dropdownExp] =
//     useState([]);
//   const dropdownRef_dropdownExp = useRef(null);

//   const options_dropdownExp = ["Option1", "Option2", "Option3"];

//   const toggleOption_dropdownExp = (option) => {
//     setSelectedOptions_dropdownExp((prev) =>
//       prev.includes(option)
//         ? prev.filter((item) => item !== option)
//         : [...prev, option],
//     );
//   };

//   useEffect(() => {
//     const handleClickOutside_dropdownExp = (event) => {
//       if (
//         dropdownRef_dropdownExp.current &&
//         !dropdownRef_dropdownExp.current.contains(event.target)
//       ) {
//         setIsOpen_dropdownExp(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside_dropdownExp);
//     return () =>
//       document.removeEventListener("mousedown", handleClickOutside_dropdownExp);
//   }, []);

//   // const handleDeleteProperty = (propertyid) => {
//   //   confirmAlert({
//   //     customUI: ({ onClose }) => {
//   //       return (
//   //         <div className="bg-white p-6 rounded-lg shadow-lg text-center">
//   //           <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
//   //           <p className="text-gray-600 mb-6">
//   //             Are you sure you want to delete this property?
//   //           </p>

//   //           <div className="flex justify-center space-x-4">
//   //             {/* Cancel */}
//   //             <button
//   //               onClick={onClose}
//   //               className="px-5 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 transition"
//   //             >
//   //               Keep It
//   //             </button>

//   //             {/* Delete */}
//   //             <button
//   //               onClick={async () => {
//   //                 try {
//   //                   const res = await axios.delete(
//   //                     `https://dash.zorbastays.com/web-backend/${propertyid}`
//   //                   );

//   //                   if (res.data.success) {
//   //                     toast.success("Property deleted successfully 🗑️");

//   //                     // refresh list
//   //                     if (active === "Villas") fetchProperties("villa");
//   //                     if (active === "Hotels") fetchProperties("hotel");
//   //                     if (active === "Resorts") fetchProperties("resort");
//   //                   } else {
//   //                     toast.error(
//   //                       res.data.error || "Failed to delete property"
//   //                     );
//   //                   }
//   //                 } catch (err) {
//   //                   console.error("Delete error:", err);
//   //                   toast.error("Error deleting property");
//   //                 }
//   //                 onClose();
//   //               }}
//   //               className="px-5 py-2 flex items-center gap-2 rounded-md bg-red-600 text-white hover:bg-black transition"
//   //             >
//   //               <FaTrash />
//   //               Delete
//   //             </button>
//   //           </div>
//   //         </div>
//   //       );
//   //     },
//   //   });
//   // };
//   const RatingConfirmation = ({ onClose, onConfirm, rating, propertyname }) => {
//     const [isSaving, setIsSaving] = useState(false);

//     const handleConfirm = async () => {
//       setIsSaving(true);
//       await onConfirm();
//       setIsSaving(false);
//       onClose();
//     };

//     return (
//       <div className="bg-white p-6 rounded-lg shadow-lg text-center max-w-sm">
//         <h2 className="text-xl font-semibold mb-4">Confirm Rating</h2>
//         <p className="text-gray-600 mb-2">
//           Set rating for{" "}
//           <span className="font-semibold text-black">{propertyname}</span> to:
//         </p>
//         <div className="flex justify-center gap-1 mb-6">
//           {[1, 2, 3, 4, 5].map((star) => (
//             <FaStar
//               key={star}
//               size={22}
//               className={star <= rating ? "text-yellow-400" : "text-gray-300"}
//             />
//           ))}
//         </div>

//         <div className="flex justify-center space-x-4">
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="px-5 py-2 rounded-md bg-gray-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleConfirm}
//             disabled={isSaving}
//             className="px-5 py-2 flex items-center gap-2 rounded-md bg-green-700 text-white hover:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             {isSaving ? "Saving..." : "Confirm"}
//           </button>
//         </div>
//       </div>
//     );
//   };
//   const DeleteConfirmation = ({ onClose, propertyid }) => {
//     const [isDeleting, setIsDeleting] = useState(false);

//     const handleDelete = async () => {
//       setIsDeleting(true);
//       try {
//         const url =
//           active === "Hotels"
//             ? `${API_URL}/deleteHotel/${propertyid}`
//             : `${API_URL}/${propertyid}`;

//         const res = await axios.delete(url);

//         if (res.data.success) {
//           toast.success("Property deleted successfully 🗑️");
//           if (active === "Villas") fetchProperties("villa");
//           if (active === "Hotels") fetchProperties("hotel");
//           if (active === "Resorts") fetchProperties("resort");
//         } else {
//           toast.error(res.data.error || "Failed to delete");
//         }
//       } catch (err) {
//         console.error("Delete error:", err);
//         toast.error("Error deleting property");
//       } finally {
//         setIsDeleting(false);
//         onClose();
//       }
//     };

//     return (
//       <div className="bg-white p-6 rounded-lg shadow-lg text-center">
//         <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
//         <p className="text-gray-600 mb-6">
//           Are you sure you want to delete this property?
//         </p>

//         <div className="flex justify-center space-x-4">
//           <button
//             onClick={onClose}
//             disabled={isDeleting}
//             className="px-5 py-2 rounded-md bg-gray-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             Keep It
//           </button>
//           <button
//             onClick={handleDelete}
//             disabled={isDeleting}
//             className="px-5 py-2 flex items-center gap-2 rounded-md bg-red-600 text-white hover:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             {isDeleting ? (
//               <>
//                 <svg
//                   className="animate-spin h-4 w-4"
//                   xmlns="http://www.w3.org/2000/svg"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                 >
//                   <circle
//                     className="opacity-25"
//                     cx="12"
//                     cy="12"
//                     r="10"
//                     stroke="currentColor"
//                     strokeWidth="4"
//                   ></circle>
//                   <path
//                     className="opacity-75"
//                     fill="currentColor"
//                     d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                   ></path>
//                 </svg>
//                 Deleting...
//               </>
//             ) : (
//               <>
//                 <FaTrash /> Delete
//               </>
//             )}
//           </button>
//         </div>
//       </div>
//     );
//   };

//   const handleDeleteProperty = (propertyid) => {
//     confirmAlert({
//       customUI: ({ onClose }) => (
//         <DeleteConfirmation onClose={onClose} propertyid={propertyid} />
//       ),
//     });
//   };
//   // const handleDeleteProperty = (propertyid) => {
//   //   confirmAlert({
//   //     customUI: ({ onClose }) => (
//   //       <div className="bg-white p-6 rounded-lg shadow-lg text-center">
//   //         <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
//   //         <p className="text-gray-600 mb-6">
//   //           Are you sure you want to delete this property?
//   //         </p>

//   //         <div className="flex justify-center space-x-4">
//   //           <button
//   //             onClick={onClose}
//   //             className="px-5 py-2 rounded-md bg-gray-800 text-white"
//   //           >
//   //             Keep It
//   //           </button>
//   //           <button
//   //             onClick={async () => {
//   //               try {
//   //                 const url =
//   //                   active === "Hotels"
//   //                     ? `https://dash.zorbastays.com/web-backend/deleteHotel/${propertyid}`
//   //                     : `https://dash.zorbastays.com/web-backend/${propertyid}`;

//   //                 const res = await axios.delete(url);

//   //                 if (res.data.success) {
//   //                   toast.success("Deleted successfully 🗑️");
//   //                   if (active === "Villas") fetchProperties("villa");
//   //                   if (active === "Hotels") fetchProperties("hotel");
//   //                   if (active === "Resorts") fetchProperties("resort");
//   //                 } else {
//   //                   toast.error(res.data.error || "Failed to delete");
//   //                 }
//   //               } catch (err) {
//   //                 console.error("Delete error:", err);
//   //                 toast.error("Error deleting property");
//   //               }
//   //               onClose();
//   //             }}
//   //             className="px-5 py-2 flex items-center gap-2 rounded-md bg-red-600 text-white hover:bg-black transition"
//   //           >
//   //             <FaTrash /> Delete
//   //           </button>
//   //         </div>
//   //       </div>
//   //     ),
//   //   });
//   // };

//   const handleToggle_topFive_resort = async (order) => {
//     const resort = table_property_resort.find((item) => item.order === order);
//     const newEnabled = !resort.topFiveEnabled;

//     if (!newEnabled) {
//       try {
//         const res = await axios.post(`${API_URL}/update-rank`, {
//           propertyid: order,
//           propertytype: "resort",
//           rank: 0,
//           mode: "topFive",
//         });
//         if (res.data.success) {
//           setTableProperty_resort((prev) =>
//             prev.map((i) =>
//               i.order === order
//                 ? { ...i, topFiveEnabled: false, topFiveValue: "" }
//                 : i,
//             ),
//           );
//         }
//       } catch (err) {
//         console.error("Failed to reset global top 5 (resort)", err);
//       }
//     } else {
//       setTableProperty_resort((prev) =>
//         prev.map((i) =>
//           i.order === order
//             ? { ...i, topFiveEnabled: true, topFiveValue: "" }
//             : i,
//         ),
//       );
//     }
//   };

//   const handleDropdownChange_topFive_resort = async (order, value) => {
//     try {
//       const res = await axios.post(`${API_URL}/update-rank`, {
//         propertyid: order,
//         propertytype: "resort",
//         rank: parseInt(value, 10),
//         mode: "topFive",
//       });

//       if (res.data.success) {
//         setTableProperty_resort((prev) =>
//           prev.map((i) =>
//             i.order === order ? { ...i, topFiveValue: value } : i,
//           ),
//         );
//       } else {
//         alert(res.data.message);
//       }
//       // window.location.reload();
//     } catch (err) {
//       console.error("Failed to update global top 5 rank (resort)", err);
//     }
//   };
//   const [availableRanks, setAvailableRanks] = useState(ALL_RANKS);
//   const [availablePropertyRanks, setAvailablePropertyRanks] =
//     useState(ALL_RANKS);
//   // const [availableVillaRanks, setAvailableVillaRanks] = useState(ALL_RANKS);
//   // const [availableHotelRanks, setAvailableHotelRanks] = useState(ALL_RANKS);
//   const [availableResortRanks, setAvailableResortRanks] = useState([ALL_RANKS]);

//   useEffect(() => {
//     if (active === "Resorts") {
//       axios
//         .get(`${API_URL}/getTakenTopProperty?propertytype=resort`)
//         .then((res) => {
//           if (res.data.success) {
//             const allRanks = [1, 2, 3, 4, 5];
//             const taken = res.data.taken || [];
//             const available = allRanks.filter((r) => !taken.includes(r));
//             setAvailableResortRanks(available);
//           }
//         })
//         .catch((err) =>
//           console.error("Failed to fetch available resort ranks", err),
//         );
//     }
//   }, [active]);
//   const [availableApartmentRanks, setAvailableApartmentRanks] = useState([
//     1, 2, 3, 4, 5,
//   ]);

//   // Add this useEffect for apartments
//   useEffect(() => {
//     if (active === "Apartments") {
//       axios
//         .get(`${API_URL}/getTakenTopProperty?propertytype=apartment`)
//         .then((res) => {
//           if (res.data.success) {
//             const allRanks = [1, 2, 3, 4, 5];
//             const taken = res.data.taken || [];
//             const available = allRanks.filter((r) => !taken.includes(r));
//             setAvailableApartmentRanks(available);
//           }
//         })
//         .catch((err) =>
//           console.error("Failed to fetch available apartment ranks", err),
//         );
//     }
//   }, [active]);

//   // Add these handler functions for apartments
//   const handleToggle_topProperty_apartment = async (order) => {
//     const apartment = table_property_apartment.find(
//       (item) => item.order === order,
//     );
//     const newEnabled = !apartment.topPropertyEnabled;

//     if (!newEnabled) {
//       // Turning OFF → clear in DB
//       try {
//         const res = await axios.post(`${API_URL}/update-rank`, {
//           propertyid: order,
//           propertytype: "apartment",
//           rank: 0,
//         });

//         if (res.data.success) {
//           const updated = table_property_apartment.map((item) =>
//             item.order === order
//               ? { ...item, topPropertyEnabled: false, topPropertyValue: "" }
//               : item,
//           );
//           setTableProperty_apartment(updated);
//           // Refresh available apartment ranks
//           const res2 = await axios.get(
//             `${API_URL}/getTakenTopProperty?propertytype=apartment`,
//           );
//           if (res2.data.success) {
//             const allRanks = [1, 2, 3, 4, 5];
//             const taken = res2.data.taken || [];
//             const available = allRanks.filter((r) => !taken.includes(r));
//             setAvailableApartmentRanks(available);
//           }
//         } else {
//           alert(res.data.message);
//         }
//       } catch (err) {
//         console.error("Failed to reset top property (apartment)", err);
//         alert("Error updating top property toggle");
//       }
//     } else {
//       // Turning ON → just enable, wait for dropdown
//       const updated = table_property_apartment.map((item) =>
//         item.order === order
//           ? { ...item, topPropertyEnabled: true, topPropertyValue: "" }
//           : item,
//       );
//       setTableProperty_apartment(updated);
//     }
//   };

//   const handleDropdownChange_topProperty_apartment = async (order, value) => {
//     try {
//       const res = await axios.post(`${API_URL}/update-rank`, {
//         propertyid: order,
//         propertytype: "apartment",
//         rank: parseInt(value, 10),
//       });

//       if (res.data.success) {
//         const updated = table_property_apartment.map((item) =>
//           item.order === order ? { ...item, topPropertyValue: value } : item,
//         );
//         setTableProperty_apartment(updated);
//         await fetchProperties(); // refresh Apartments table
//         // Refresh available apartment ranks
//         const res2 = await axios.get(
//           `${API_URL}/getTakenTopProperty?propertytype=apartment`,
//         );
//         if (res2.data.success) {
//           const allRanks = [1, 2, 3, 4, 5];
//           const taken = res2.data.taken || [];
//           const available = allRanks.filter((r) => !taken.includes(r));
//           setAvailableApartmentRanks(available);
//         }
//       } else {
//         alert(res.data.message);
//       }
//     } catch (err) {
//       console.error("Failed to update apartment rank", err);
//       alert("Error updating apartment rank");
//     }
//   };

//   const handleToggle_topFive_apartment = async (order) => {
//     const apartment = table_property_apartment.find(
//       (item) => item.order === order,
//     );
//     const newEnabled = !apartment.topFiveEnabled;

//     if (!newEnabled) {
//       try {
//         const res = await axios.post(`${API_URL}/update-rank`, {
//           propertyid: order,
//           propertytype: "apartment",
//           rank: 0,
//           mode: "topFive",
//         });
//         if (res.data.success) {
//           setTableProperty_apartment((prev) =>
//             prev.map((i) =>
//               i.order === order
//                 ? { ...i, topFiveEnabled: false, topFiveValue: "" }
//                 : i,
//             ),
//           );
//           // Refresh available global ranks
//           const res2 = await axios.get(`${API_URL}/getTakenTopFive`);
//           if (res2.data.success) {
//             const taken = res2.data.taken || [];
//             const allRanks = [1, 2, 3, 4, 5];
//             const freeRanks = allRanks.filter((r) => !taken.includes(r));
//             setAvailableRanks(freeRanks);
//           }
//         }
//       } catch (err) {
//         console.error("Failed to reset global top 5 (apartment)", err);
//       }
//     } else {
//       setTableProperty_apartment((prev) =>
//         prev.map((i) =>
//           i.order === order
//             ? { ...i, topFiveEnabled: true, topFiveValue: "" }
//             : i,
//         ),
//       );
//     }
//   };

//   const handleDropdownChange_topFive_apartment = async (order, value) => {
//     try {
//       const res = await axios.post(`${API_URL}/update-rank`, {
//         propertyid: order,
//         propertytype: "apartment",
//         rank: parseInt(value, 10),
//         mode: "topFive",
//       });

//       if (res.data.success) {
//         setTableProperty_apartment((prev) =>
//           prev.map((i) =>
//             i.order === order ? { ...i, topFiveValue: value } : i,
//           ),
//         );
//         // Refresh available global ranks instead of reloading
//         const res2 = await axios.get(`${API_URL}/getTakenTopFive`);
//         if (res2.data.success) {
//           const taken = res2.data.taken || [];
//           const allRanks = [1, 2, 3, 4, 5];
//           const freeRanks = allRanks.filter((r) => !taken.includes(r));
//           setAvailableRanks(freeRanks);
//         }
//       } else {
//         alert(res.data.message);
//       }
//     } catch (err) {
//       console.error("Failed to update global top 5 rank (apartment)", err);
//     }
//   };
//   const handleAddProperty = () => {
//     if (active === "Hotels") {
//       navigate("/AddHotelElementor");
//     } else {
//       navigate("/AddPropertyDetailElementor");
//     }
//   };
//   const [analytics, setAnalytics] = useState({
//     totalvillas: 0,
//     totalhotels: 0,
//     avgscore: 0,
//   });

//   useEffect(() => {
//     const fetchAnalytics = async () => {
//       try {
//         const res = await axios.get(`${API_URL}/property-completion-box`);

//         if (res.data.success) {
//           setAnalytics(res.data.analytics);
//         }
//       } catch (err) {
//         console.error("Error fetching analytics:", err);
//       }
//     };

//     fetchAnalytics();
//   }, []);

//   // format numbers with commas
//   const formatNumber = (num) =>
//     new Intl.NumberFormat("en-IN").format(Number(num));

//   const handleAvailabilityToggle = async (item) => {
//     const newAvailability = item.availability ? 0 : 1;
//     const propertytype = active === "Hotels" ? "hotel" : "villa";

//     // Optimistically update UI first
//     const updateState =
//       active === "Hotels" ? setTableProperty_hotel : setTableProperty_villa;
//     updateState((prev) =>
//       prev.map((row) =>
//         row.order === item.order
//           ? { ...row, availability: newAvailability }
//           : row,
//       ),
//     );

//     try {
//       const res = await axios.post(`${API_URL}/update-availability`, {
//         propertyid: item.order,
//         availability: newAvailability,
//       });

//       if (!res.data.success) {
//         // revert on failure
//         updateState((prev) =>
//           prev.map((row) =>
//             row.order === item.order
//               ? { ...row, availability: item.availability }
//               : row,
//           ),
//         );
//         alert(res.data.message || "Failed to update availability");
//       }
//     } catch (err) {
//       console.error("Failed to update availability", err);
//       // revert on error
//       updateState((prev) =>
//         prev.map((row) =>
//           row.order === item.order
//             ? { ...row, availability: item.availability }
//             : row,
//         ),
//       );
//       alert("Error updating availability");
//     }
//   };
//   const handleRatingChange = async (order, propertytype, rating) => {
//     const updateState =
//       propertytype === "hotel"
//         ? setTableProperty_hotel
//         : setTableProperty_villa;

//     // optimistic UI update
//     updateState((prev) =>
//       prev.map((item) => (item.order === order ? { ...item, rating } : item)),
//     );

//     try {
//       const res = await axios.post(`${API_URL}/update-rating`, {
//         propertyid: order,
//         rating,
//       });
//       if (res.data.success) {
//         toast.success("Rating updated successfully ⭐");
//       } else {
//         alert(res.data.message || "Failed to update rating");
//         updateState((prev) =>
//           prev.map((item) =>
//             item.order === order ? { ...item, rating: item.rating } : item,
//           ),
//         );
//       }
//     } catch (err) {
//       console.error("Failed to update rating", err);
//       alert("Error updating rating");
//     }
//   };
//   const confirmRatingChange = (order, propertytype, rating, propertyname) => {
//     confirmAlert({
//       customUI: ({ onClose }) => (
//         <RatingConfirmation
//           onClose={onClose}
//           rating={rating}
//           propertyname={propertyname}
//           onConfirm={() => handleRatingChange(order, propertytype, rating)}
//         />
//       ),
//     });
//   };
//   const handleSetRank = async (order, type, mode, rank) => {
//     const setTable =
//       type === "hotel" ? setTableProperty_hotel : setTableProperty_villa;
//     const isGlobal = mode === "topfive";

//     try {
//       const res = await axios.post(`${API_URL}/update-rank`, {
//         propertyid: order,
//         propertytype: type,
//         rank,
//         mode,
//       });

//       if (!res.data.success) {
//         toast.error(res.data.message || "Failed to update rank");
//         return;
//       }

//       setTable((prev) =>
//         prev.map((i) => {
//           if (i.order !== order) return i;
//           return isGlobal
//             ? {
//                 ...i,
//                 topFiveEnabled: rank > 0,
//                 topFiveValue: rank > 0 ? rank : "",
//               }
//             : {
//                 ...i,
//                 topPropertyEnabled: rank > 0,
//                 topPropertyValue: rank > 0 ? rank : "",
//               };
//         }),
//       );
//       toast.success(rank > 0 ? `Rank set to ${rank}` : "Rank removed");
//       loadAvailableRanks();
//     } catch (err) {
//       // axios throws on 400, so the "already taken" message lives here
//       toast.error(err.response?.data?.message || "Error updating rank");
//     }
//   };
//   return (
//     <div className="flex bg-[#F8F9FA]">
//       <Menubar
//         isOpen={isSidebarOpen}
//         setIsOpen={setIsSidebarOpen}
//         isMobile={isMobile}
//       />

//       <main
//         className={`p-5 lg:p-6 transition-all duration-500 w-full ${
//           isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
//         }`}
//       >
//         <Outlet />

//         {/* Header */}
//         <div className="flex justify-between gap-5 items-start lg:items-center">
//           <div className="flex flex-col lg:flex-row gap-5 items-start lg:items-center">
//             <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
//               Properties
//             </p>
//           </div>

//           <div className="flex items-center gap-1">
//             <div className="flex items-center">
//               <div className="mr-2 p-2 overflow-hidden w-8 h-8 hover:w-[120px] sm:hover:w-[250px] hover:border hover:border-[#1D2826] hover:shadow-[2px_2px_20px_rgba(0,0,0,0.08)] rounded-full flex group items-center hover:duration-300 duration-300">
//                 <div className="flex items-center justify-center fill-[#1D2826]">
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     id="Isolation_Mode"
//                     data-name="Isolation Mode"
//                     viewBox="0 0 24 24"
//                     width="18"
//                     height="18"
//                   >
//                     <path d="M18.9,16.776A10.539,10.539,0,1,0,16.776,18.9l5.1,5.1L24,21.88ZM10.5,18A7.5,7.5,0,1,1,18,10.5,7.507,7.507,0,0,1,10.5,18Z"></path>
//                   </svg>
//                 </div>
//                 <input
//                   type="text"
//                   placeholder={`Search In ${active}...`}
//                   value={searchQuery}
//                   onChange={(e) => setSearchQuery(e.target.value)}
//                   className="border-none outline-none text-sm bg-transparent w-full font-normal px-2 focus:ring-0"
//                 />
//               </div>
//             </div>
//           </div>
//         </div>
//         {/* Boxes */}
//         <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 items-center gap-5 text-gray-700 font-semibold mt-5">
//           <div className="flex items-center justify-between gap-5 bg-[#DAE0CE] py-2 px-4 rounded-lg">
//             <div>
//               <p>Total Villas</p>
//               <p className="mt-2 text-lg text-black">
//                 {formatNumber(analytics.totalvillas)}
//                 {/* <sup className="text-green-500">+55%</sup> */}
//               </p>
//             </div>

//             <div>
//               <MdHolidayVillage className="bg-[#3C4C4E] text-3xl text-white p-1.5 rounded-md" />
//             </div>
//           </div>

//           <div className="flex items-center justify-between gap-5 bg-[#DAE0CE] py-2 px-4 rounded-lg">
//             <div>
//               <p>Total Hotels </p>
//               <p className="mt-2 text-lg text-black">
//                 {formatNumber(analytics.totalhotels)}
//                 {/* <sup className="text-green-500">+5%</sup> */}
//               </p>
//             </div>

//             <div>
//               <FaHotel className="bg-[#3C4C4E] text-3xl text-white p-1.5 rounded-md" />
//             </div>
//           </div>

//           <div className="flex items-center justify-between gap-5 bg-[#DAE0CE] py-2 px-4 rounded-lg">
//             <div>
//               <p>Average Score</p>
//               <p className="mt-2 text-lg text-black">
//                 {analytics.avgscore}%
//                 {/* <sup className="text-green-500">+8%</sup> */}
//               </p>
//             </div>

//             <div>
//               <VscPercentage className="bg-[#3C4C4E] text-3xl text-white p-1.5 rounded-md" />
//             </div>
//           </div>
//         </div>
//         {/* Segmented buttons */}
//         <div className=" flex flex-col sm:flex-row sm:items-center justify-between gap-5 mt-8">
//           <div>
//             <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm font-medium shadow-sm">
//               {options.map((option) => (
//                 <button
//                   key={option}
//                   onClick={() => setActive(option)}
//                   className={`px-4 py-2 transition-colors duration-200 focus:outline-none ${
//                     active === option
//                       ? "bg-[#1D2826] text-white"
//                       : "bg-white text-gray-500 hover:bg-green-50"
//                   }`}
//                 >
//                   {option}
//                 </button>
//               ))}
//             </div>
//           </div>

//           {/* Button */}
//           <div className="flex gap-2">
//             <button
//               onClick={() => window.open("/SmartScheduler", "_blank")}
//               className="flex items-center gap-2 px-6 z-30 py-2 border-2 border-[#1D2826] text-[#1D2826] rounded-lg text-center hover:text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-900 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-500 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//             >
//               <FaWandMagicSparkles /> <span>Smart Scheduler</span>
//             </button>
//             <button
//               onClick={handleAddProperty}
//               className="px-6 z-30 py-2 bg-[#1D2826] rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-900 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//             >
//               + Add Property
//             </button>

//             <div className="relative z-50">
//               {isOpen_popupForm && (
//                 <div
//                   onClick={() => setIsOpen_popupForm(false)}
//                   className="fixed inset-0 bg-black bg-opacity-30 z-40 backdrop_popupForm"
//                 />
//               )}

//               <div
//                 className={`fixed top-0 right-0 h-full w-[85%] md:w-[640px] bg-white z-50 shadow-lg transform transition-transform duration-500 ease-in-out ${
//                   isOpen_popupForm ? "translate-x-0" : "translate-x-full"
//                 } panel_popupForm`}
//               >
//                 <div className="p-4 flex justify-between items-center border-b header_popupForm">
//                   <h2 className="text-[#1D2826] text-lg font-semibold">
//                     Add New Property
//                   </h2>
//                   <button
//                     onClick={() => setIsOpen_popupForm(false)}
//                     className="text-gray-500 hover:text-black text-xl"
//                   >
//                     <MdCancel />
//                   </button>
//                 </div>

//                 <div className="p-4">
//                   <div className="font-semibold text-gray-500 text-sm text-right">
//                     Property ID:{" "}
//                     <span className="text-black my-1">10294837</span>
//                   </div>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 items-center max-h-[85vh] overflow-y-auto py-2">
//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Property Name
//                       </label>
//                       <input
//                         placeholder="Enter property name"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Property Code
//                       </label>
//                       <input
//                         placeholder="Enter property code"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Headline
//                       </label>
//                       <textarea
//                         placeholder="Enter headline"
//                         rows="3"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Description
//                       </label>
//                       <textarea
//                         placeholder="Enter description"
//                         rows="3"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Current Price
//                       </label>
//                       <input
//                         placeholder="Enter current price"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Default Price
//                       </label>
//                       <input
//                         placeholder="Enter default price"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Security Deposit
//                       </label>
//                       <input
//                         placeholder="Enter security deposit"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Max Adults
//                       </label>
//                       <input
//                         placeholder="Enter max adults"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Max Children
//                       </label>
//                       <input
//                         placeholder="Enter max children"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Max Bedroom
//                       </label>
//                       <input
//                         placeholder="Enter max bedroom"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Max Bathroom
//                       </label>
//                       <input
//                         placeholder="Enter max bathroom"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Map Location
//                       </label>
//                       <input
//                         placeholder="Enter map location"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label
//                         for="input"
//                         className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                       >
//                         Youtube Link
//                       </label>
//                       <input
//                         placeholder="Enter youtube link"
//                         name="input"
//                         className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                       />
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                         Select Location
//                       </label>

//                       <select className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md">
//                         <option value="">Select your location</option>
//                         <option value="">option1</option>
//                         <option value="">option2</option>
//                         <option value="">option3</option>
//                       </select>
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                         Property Type
//                       </label>

//                       <select className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md">
//                         <option value="">Select your property type</option>
//                         <option value="">option1</option>
//                         <option value="">option2</option>
//                         <option value="">option3</option>
//                       </select>
//                     </div>

//                     <div
//                       className="relative w-full"
//                       ref={dropdownRef_dropdownExp}
//                     >
//                       <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                         Experience
//                       </label>

//                       <div
//                         onClick={() => setIsOpen_dropdownExp((prev) => !prev)}
//                         className="border border-gray-400 p-3 rounded-lg w-full cursor-pointer text-sm bg-white focus:outline-none"
//                       >
//                         {selectedOptions_dropdownExp.length > 0
//                           ? selectedOptions_dropdownExp.join(", ")
//                           : "Select your experience(s)"}
//                       </div>

//                       {isOpen_dropdownExp && (
//                         <div className="absolute top-full left-0 w-full bg-white border border-gray-300 mt-1 rounded-md shadow-md z-10">
//                           {options_dropdownExp.map((option) => (
//                             <label
//                               key={option}
//                               className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                             >
//                               <input
//                                 type="checkbox"
//                                 checked={selectedOptions_dropdownExp.includes(
//                                   option,
//                                 )}
//                                 onChange={() =>
//                                   toggleOption_dropdownExp(option)
//                                 }
//                                 className="accent-black"
//                               />
//                               {option}
//                             </label>
//                           ))}
//                         </div>
//                       )}
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                         destination
//                       </label>

//                       <select className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md">
//                         <option value="">Select your destination</option>
//                         <option value="">Option1</option>
//                         <option value="">Option2</option>
//                         <option value="">Option3</option>
//                       </select>
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                         Season Type
//                       </label>

//                       <select className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md">
//                         <option value="">Select season type</option>
//                         <option value="">Option1</option>
//                         <option value="">Option2</option>
//                         <option value="">Option3</option>
//                       </select>
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                         Payment Status
//                       </label>

//                       <select className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md">
//                         <option value="">Select payment status</option>
//                         <option value="">Option1</option>
//                         <option value="">Option2</option>
//                         <option value="">Option3</option>
//                       </select>
//                     </div>

//                     <div className="flex flex-col w-full">
//                       <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                         Property
//                       </label>

//                       <select className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md">
//                         <option value="">Select a property</option>
//                         <option value="Villa Serenity">Villa Serenity</option>
//                         <option value="Villa Arcadia">Villa Arcadia</option>
//                         <option value="Villa Mandovi">Villa Mandovi</option>
//                         <option value="Villa Sal">Villa Sal</option>
//                       </select>
//                     </div>

//                     <div className="grid grid-cols-2 gap-3 mt-3">
//                       <button
//                         onClick={() => setIsOpen_popupForm(false)}
//                         className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//                       >
//                         Cancel
//                       </button>

//                       <button className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm">
//                         Save
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Table */}
//         <div>
//           {active === "Villas" && (
//             <div className="shadow-md rounded-lg mt-5">
//               <div className="overflow-x-auto">
//                 <table className="w-full text-sm text-left rtl:text-right text-gray-500">
//                   <thead className="text-xs text-gray-400 uppercase bg-green-50 border-b">
//                     <tr>
//                       <th className="p-4 w-10">
//                         <input
//                           type="checkbox"
//                           onChange={handleSelectAll_villa}
//                           checked={
//                             selectedRows_villa.length ===
//                               current_property_villa.length &&
//                             current_property_villa.length > 0
//                           }
//                         />
//                       </th>
//                       <th className="p-4 w-1/10">Villa Name</th>

//                       <th className="p-4 w-1/10">Property Type</th>

//                       <th className="p-4 w-1/10">Property Owner</th>
//                       <th className="p-4 w-1/10">Location</th>
//                       <th className="p-4 w-1/10">Rating</th>
//                       <th className="p-4 w-1/10">Availability</th>
//                       <th className="p-4 w-1/10">Score</th>
//                       <th className="p-4 w-1/10">Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {showLoader ? (
//                       <tr>
//                         <td colSpan="10" className="text-center py-10">
//                           <div className="flex flex-col items-center justify-center gap-3">
//                             <Loader
//                               label="Loading..."
//                               fullScreen={false}
//                               className="py-2"
//                             />
//                           </div>
//                         </td>
//                       </tr>
//                     ) : current_property_villa.length === 0 ? (
//                       <tr>
//                         <td
//                           colSpan="8"
//                           className="text-center py-6 text-gray-500"
//                         >
//                           No properties in this category
//                         </td>
//                       </tr>
//                     ) : (
//                       current_property_villa.map((villa, index) => (
//                         <tr
//                           key={index}
//                           className="bg-white even:bg-gray-50 border-b border-gray-200 hover:bg-gray-100 text-gray-800"
//                         >
//                           <td className="px-4 py-3">
//                             <input
//                               type="checkbox"
//                               onChange={() =>
//                                 handleRowCheckboxChange_villa(villa.order)
//                               }
//                               checked={selectedRows_villa.includes(villa.order)}
//                             />
//                           </td>
//                           <td
//                             className="px-4 py-3"
//                             data-full-content={villa.customer}
//                           >
//                             <RankPopover
//                               name={villa.customer}
//                               topValue={villa.topFiveValue}
//                               typeValue={villa.topPropertyValue}
//                               typeLabel="Villa Rank"
//                               availableTop={availableRanks}
//                               availableType={availablePropertyRanks}
//                               onSelectTop={(rank) => {
//                                 if (
//                                   villa.propertytype?.toLowerCase() ===
//                                   "apartment"
//                                 ) {
//                                   toast.info(
//                                     "You cannot update apartment rank.",
//                                   );
//                                   return;
//                                 }
//                                 handleSetRank(
//                                   villa.order,
//                                   "villa",
//                                   "topfive",
//                                   rank,
//                                 );
//                               }}
//                               onSelectType={(rank) => {
//                                 if (
//                                   villa.propertytype?.toLowerCase() ===
//                                   "apartment"
//                                 ) {
//                                   toast.info(
//                                     "You cannot update apartment rank.",
//                                   );
//                                   return;
//                                 }
//                                 handleSetRank(
//                                   villa.order,
//                                   "villa",
//                                   "property",
//                                   rank,
//                                 );
//                               }}
//                             />
//                           </td>

//                           <td
//                             className="px-4 py-3 text-center"
//                             data-full-content={villa.propertytype}
//                           >
//                             <Tooltip
//                               title={villa.propertytype}
//                               placement="left"
//                             >
//                               {villa.propertytype}
//                             </Tooltip>
//                           </td>

//                           <td className="px-4 py-3">
//                             <Tooltip
//                               title={villa.propertyowner || "N/A"}
//                               placement="left"
//                             >
//                               {villa.propertyowner || "N/A"}
//                             </Tooltip>
//                           </td>
//                           <td className="px-4 py-3">
//                             <Tooltip
//                               title={villa.location || "N/A"}
//                               placement="left"
//                             >
//                               {villa.location || "N/A"}
//                             </Tooltip>
//                           </td>
//                           {/* Rating */}
//                           <td className="px-4 py-3">
//                             <StarRating
//                               rating={villa.rating}
//                               onRate={(val) =>
//                                 confirmRatingChange(
//                                   villa.order,
//                                   "villa",
//                                   val,
//                                   villa.customer,
//                                 )
//                               }
//                             />
//                           </td>

//                           {/* <td className="px-4 py-3">
//                             <div className="flex justify-between items-center">
//                               <button
//                                 className={`w-20 px-1 py-1 rounded-lg text-xs scale-90 cursor-default ${
//                                   statusColors[villa.status]
//                                 }`}
//                               >
//                                 {villa.status}
//                               </button>
//                             </div>
//                           </td> */}

//                           {/* Online offline toggle here */}
//                           <td className="px-4 py-3">
//                             <Tooltip
//                               content={
//                                 villa.availability
//                                   ? "Click to Disable"
//                                   : "Click to Make Live"
//                               }
//                               placement="left"
//                             >
//                               <button
//                                 onClick={() => handleAvailabilityToggle(villa)}
//                                 className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 transition-all duration-300 hover:scale-105 ${
//                                   villa.availability
//                                     ? "border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
//                                     : "border-gray-200 bg-gray-100 hover:bg-gray-200"
//                                 }`}
//                               >
//                                 <span
//                                   className={`h-2 w-2 rounded-full ${
//                                     villa.availability
//                                       ? "bg-emerald-500 animate-pulse"
//                                       : "bg-gray-400"
//                                   }`}
//                                 ></span>

//                                 <span
//                                   className={`text-xs font-semibold ${
//                                     villa.availability
//                                       ? "text-emerald-700"
//                                       : "text-gray-600"
//                                   }`}
//                                 >
//                                   {villa.availability ? "Live" : "Disabled"}
//                                 </span>
//                               </button>
//                             </Tooltip>
//                           </td>
//                           {/* <td className="px-4 py-3">
//                             <Tooltip title={villa.payment} placement="left">
//                               {villa.payment}
//                             </Tooltip>
//                           </td> */}
//                           <td className="px-4 py-3 text-left">
//                             <ProgressBar
//                               value={villaProgress[villa.order] ?? 0}
//                             />
//                           </td>

//                           <td>
//                             <div className="grid grid-cols-2 gap-y-1 my-2">
//                               <Tooltip title="View Property" placement="top">
//                                 <button
//                                   className="pl-5 py-1 text-gray-400 hover:text-sky-500 hover:scale-125 transition-all"
//                                   onClick={() => {
//                                     sessionStorage.setItem(
//                                       "villaPage",
//                                       currentPage_villa,
//                                     );
//                                     navigate("/EditPropertyDetailElementor", {
//                                       state: {
//                                         propertyid: villa.order,
//                                         mode: "view",
//                                       },
//                                     });
//                                   }}
//                                 >
//                                   <FaEye
//                                     size={22}
//                                     className="border border-sky-300 rounded-full p-0.5"
//                                   />
//                                 </button>
//                               </Tooltip>

//                               <Tooltip title="Edit Property" placement="top">
//                                 <button
//                                   className="pl-1 py-1 text-gray-400 hover:text-yellow-300 hover:scale-125 transition-all"
//                                   // onClick={() =>
//                                   //   navigate("/EditPropertyElementor", {
//                                   //     state: { propertyid: villa.order },
//                                   //   })
//                                   // }
//                                   onClick={() => {
//                                     sessionStorage.setItem(
//                                       "villaPage",
//                                       currentPage_villa,
//                                     ); // ← change here
//                                     navigate("/EditPropertyDetailElementor", {
//                                       state: { propertyid: villa.order },
//                                     });
//                                   }}
//                                 >
//                                   <RiFileEditFill
//                                     size={22}
//                                     className="border border-yellow-300 rounded-full p-0.5"
//                                   />
//                                 </button>
//                               </Tooltip>
//                               <Tooltip title="Quick Brochure" placement="top">
//                                 {/* <button
//                                   onClick={() =>
//                                     navigate(`/NewQuickBrochure/${villa.order}`)
//                                   }

//                                   className="pl-5 py-1 text-gray-400 hover:text-purple-500 hover:scale-125 transition-all mail-button"
//                                 > */}
//                                 <button
//                                   onClick={() =>
//                                     window.open(
//                                       `/NewQuickBrochure/${villa.order}`,
//                                       "_blank",
//                                     )
//                                   }
//                                   className="pl-5 py-1 text-gray-400 hover:text-purple-500 hover:scale-125 transition-all mail-button"
//                                 >
//                                   <FaFileArrowDown
//                                     size={22}
//                                     className="border border-purple-300 rounded-full p-[3px]"
//                                   />
//                                 </button>
//                               </Tooltip>
//                               <Tooltip title="Delete Property" placement="top">
//                                 <button
//                                   onClick={() =>
//                                     handleDeleteProperty(villa.order)
//                                   }
//                                   className="pl-1 py-1 text-gray-400 hover:text-red-500 hover:scale-125 transition-all"
//                                 >
//                                   <MdDelete
//                                     size={22}
//                                     className="border border-red-300 rounded-full p-0.5"
//                                   />
//                                 </button>
//                               </Tooltip>
//                             </div>
//                           </td>
//                         </tr>
//                       ))
//                     )}
//                   </tbody>
//                 </table>
//               </div>
//               {/*  Pagination footer villa */}
//               <nav
//                 className="flex items-center flex-column flex-wrap md:flex-row justify-between rounded-b-lg px-2 py-1 bg-[#f7f7f7]"
//                 aria-label="Table navigation"
//               >
//                 <span className="text-xs font-normal text-gray-500 mb-4 md:mb-0 block w-full md:inline md:w-auto">
//                   Showing{" "}
//                   <span className="font-semibold text-gray-700">
//                     {indexOfFirstTable_villa + 1}-
//                     {Math.min(
//                       indexOfLastTable_villa,
//                       filtered_property_villa.length,
//                     )}
//                   </span>{" "}
//                   of{" "}
//                   <span className="font-semibold text-gray-700">
//                     {filtered_property_villa.length}
//                   </span>
//                 </span>

//                 <ul className="inline-flex -space-x-px rtl:space-x-reverse text-xs h-8">
//                   <li>
//                     <button
//                       onClick={handlePrevPage_villa}
//                       disabled={currentPage_villa === 1}
//                       className="flex items-center justify-center px-1 h-8 ms-0 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700"
//                     >
//                       <MdKeyboardDoubleArrowLeft />
//                     </button>
//                   </li>

//                   {generatePageNumbers_villa().map((page, index) =>
//                     page === "..." ? (
//                       <li
//                         key={index}
//                         className="px-1 h-8 flex items-center justify-center text-gray-500 bg-[#f7f7f7] border-gray-300"
//                       >
//                         <span>...</span>
//                       </li>
//                     ) : (
//                       <li key={index}>
//                         <button
//                           onClick={() => handlePageChange_villa(page)}
//                           className={`flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 hover:bg-gray-100 hover:text-gray-700 ${
//                             currentPage_villa === page
//                               ? "text-green-500 underline underline-offset-2"
//                               : ""
//                           }`}
//                         >
//                           {page}
//                         </button>
//                       </li>
//                     ),
//                   )}

//                   <li>
//                     <button
//                       onClick={handleNextPage_villa}
//                       disabled={currentPage_villa === totalPages_villa}
//                       className="flex items-center justify-center px-1 h-8 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700"
//                     >
//                       <MdKeyboardDoubleArrowRight />
//                     </button>
//                   </li>
//                 </ul>
//               </nav>
//             </div>
//           )}

//           {active === "Hotels" && (
//             <div className="shadow-md rounded-lg mt-5">
//               <div className="overflow-x-auto">
//                 <table className="w-full text-sm text-left rtl:text-right text-gray-500">
//                   <thead className="text-xs text-gray-400 uppercase bg-green-50 border-b">
//                     <tr>
//                       <th className="p-4 w-10">
//                         <input
//                           type="checkbox"
//                           onChange={handleSelectAll_hotel}
//                           checked={
//                             selectedRows_hotel.length ===
//                               current_property_hotel.length &&
//                             current_property_hotel.length > 0
//                           }
//                         />
//                       </th>
//                       <th className="p-4 w-1/10">Hotel Name</th>

//                       <th className="p-4 w-1/10">Property Owner</th>
//                       <th className="p-4 w-1/10">Location</th>
//                       <th className="p-4 w-1/10">Rating</th>

//                       <th className="p-4 w-1/10">Availability</th>
//                       <th className="p-4 w-1/10">Score</th>
//                       <th className="p-4 w-1/10">Actions</th>
//                     </tr>
//                   </thead>

//                   <tbody>
//                     {showLoader ? (
//                       <tr>
//                         <td colSpan="8" className="text-center py-10">
//                           <div className="flex flex-col items-center justify-center gap-3">
//                             <div className="flex flex-col items-center justify-center gap-3">
//                               <Loader
//                                 label="Loading..."
//                                 fullScreen={false}
//                                 className="py-2"
//                               />
//                             </div>
//                           </div>
//                         </td>
//                       </tr>
//                     ) : current_property_hotel.length === 0 ? (
//                       <tr>
//                         <td
//                           colSpan="8"
//                           className="text-center py-6 text-gray-500"
//                         >
//                           No properties in this category
//                         </td>
//                       </tr>
//                     ) : (
//                       current_property_hotel.map((hotel, index) => (
//                         <tr
//                           key={index}
//                           className="bg-white even:bg-gray-50 border-b border-gray-200 hover:bg-gray-100 text-gray-800"
//                         >
//                           <td className="px-4 py-3">
//                             <input
//                               type="checkbox"
//                               onChange={() =>
//                                 handleRowCheckboxChange_hotel(hotel.order)
//                               }
//                               checked={selectedRows_hotel.includes(hotel.order)}
//                             />
//                           </td>

//                           <td
//                             className="px-4 py-3"
//                             data-full-content={hotel.customer}
//                           >
//                             <RankPopover
//                               name={hotel.customer}
//                               topValue={hotel.topFiveValue}
//                               typeValue={hotel.topPropertyValue}
//                               typeLabel="Hotel Rank"
//                               availableTop={availableRanks}
//                               availableType={availablePropertyRanks}
//                               onSelectTop={(rank) =>
//                                 handleSetRank(
//                                   hotel.order,
//                                   "hotel",
//                                   "topfive",
//                                   rank,
//                                 )
//                               }
//                               onSelectType={(rank) =>
//                                 handleSetRank(
//                                   hotel.order,
//                                   "hotel",
//                                   "property",
//                                   rank,
//                                 )
//                               }
//                             />
//                           </td>
//                           <td className="px-4 py-3">
//                             <Tooltip
//                               title={hotel.propertyowner || "N/A"}
//                               placement="left"
//                             >
//                               {hotel.propertyowner || "N/A"}
//                             </Tooltip>
//                           </td>
//                           <td className="px-4 py-3">
//                             <Tooltip
//                               title={hotel.location || "N/A"}
//                               placement="left"
//                             >
//                               {hotel.location || "N/A"}
//                             </Tooltip>
//                           </td>
//                           {/* Rating */}
//                           <td className="px-4 py-3">
//                             <StarRating
//                               rating={hotel.rating}
//                               onRate={(val) =>
//                                 confirmRatingChange(
//                                   hotel.order,
//                                   "hotel",
//                                   val,
//                                   hotel.customer,
//                                 )
//                               }
//                             />
//                           </td>
//                           {/* <td className="px-4 py-3">
//                             <div className="flex justify-between items-center">
//                               <button
//                                 className={`w-20 px-1 py-1 rounded-lg text-xs scale-90 cursor-default ${
//                                   statusColors[hotel.status]
//                                 }`}
//                               >
//                                 {hotel.status}
//                               </button>
//                             </div>
//                           </td> */}
//                           <td className="px-4 py-3">
//                             <Tooltip
//                               content={
//                                 hotel.availability
//                                   ? "Click to Disable"
//                                   : "Click to Make Live"
//                               }
//                               placement="left"
//                             >
//                               <button
//                                 onClick={() => handleAvailabilityToggle(hotel)}
//                                 className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 transition-all duration-300 hover:scale-105 ${
//                                   hotel.availability
//                                     ? "border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
//                                     : "border-gray-200 bg-gray-100 hover:bg-gray-200"
//                                 }`}
//                               >
//                                 <span
//                                   className={`h-2 w-2 rounded-full ${
//                                     hotel.availability
//                                       ? "bg-emerald-500 animate-pulse"
//                                       : "bg-gray-400"
//                                   }`}
//                                 ></span>
//                                 <span
//                                   className={`text-xs font-semibold ${
//                                     hotel.availability
//                                       ? "text-emerald-700"
//                                       : "text-gray-600"
//                                   }`}
//                                 >
//                                   {hotel.availability ? "Live" : "Disabled"}
//                                 </span>
//                               </button>
//                             </Tooltip>
//                           </td>
//                           {/* <td className="px-4 py-3">
//                             <Tooltip title={hotel.payment} placement="left">
//                               {hotel.payment}
//                             </Tooltip>
//                           </td> */}
//                           <td className="px-4 py-3 text-left">
//                             <ProgressBar
//                               value={hotelProgress[hotel.order] ?? 0}
//                             />
//                           </td>
//                           <td>
//                             <div className="flex">
//                               <Tooltip title="View Hotel" placement="top">
//                                 <button
//                                   className="px-2 py-1 text-gray-400 hover:text-black hover:dark:text-white hover:scale-125 transition-all duration-300"
//                                   // onClick={() =>
//                                   //   navigate("/EditPropertyElementor")
//                                   // }
//                                   onClick={() =>
//                                     navigate("/EditPropertyDetailElementor", {
//                                       state: { propertyid: hotel.order },
//                                     })
//                                   }
//                                 >
//                                   <FaEye size={15} />
//                                 </button>
//                               </Tooltip>
//                               <Tooltip title="Edit Hotel" placement="top">
//                                 <button
//                                   className="px-2 py-1 text-gray-400 hover:text-black hover:dark:text-white hover:scale-125 transition-all duration-300"
//                                   // onClick={() =>
//                                   //   navigate("/EditPropertyElementor")
//                                   // }
//                                   // onClick={() =>
//                                   //   navigate("/EditPropertyElementor", {
//                                   //     state: { propertyid: hotel.order },
//                                   //   })
//                                   // }
//                                   onClick={() => {
//                                     if (active === "Hotels") {
//                                       navigate("/EditHotelElementor", {
//                                         state: { propertyid: hotel.order },
//                                       });
//                                     } else {
//                                       navigate("/EditPropertyDetailElementor", {
//                                         state: { propertyid: hotel.order },
//                                       });
//                                     }
//                                   }}
//                                 >
//                                   <FaEdit size={14} />
//                                 </button>
//                               </Tooltip>
//                               <Tooltip title="Delete Hotel" placement="top">
//                                 <button
//                                   onClick={() =>
//                                     handleDeleteProperty(hotel.order)
//                                   }
//                                   className="px-2 py-1 text-gray-400 hover:text-black hover:dark:text-white hover:scale-125 transition-all duration-300"
//                                 >
//                                   <MdDelete size={15} />
//                                 </button>
//                               </Tooltip>
//                             </div>
//                           </td>
//                         </tr>
//                       ))
//                     )}
//                   </tbody>
//                 </table>
//               </div>
//               {/*  Pagination footer for Hotels */}
//               <nav
//                 className="flex items-center flex-column flex-wrap md:flex-row justify-between rounded-b-lg px-2 py-1 bg-[#f7f7f7]"
//                 aria-label="Table navigation"
//               >
//                 <span className="text-xs font-normal text-gray-500 mb-4 md:mb-0 block w-full md:inline md:w-auto">
//                   Showing{" "}
//                   <span className="font-semibold text-gray-700">
//                     {indexOfFirstTable_hotel + 1}-
//                     {Math.min(
//                       indexOfLastTable_hotel,
//                       filtered_property_hotel.length,
//                     )}
//                   </span>{" "}
//                   of{" "}
//                   <span className="font-semibold text-gray-700">
//                     {filtered_property_hotel.length}
//                   </span>
//                 </span>

//                 <ul className="inline-flex -space-x-px rtl:space-x-reverse text-xs h-8">
//                   <li>
//                     <button
//                       onClick={handlePrevPage_hotel}
//                       disabled={currentPage_hotel === 1}
//                       className="flex items-center justify-center px-1 h-8 ms-0 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700"
//                     >
//                       <MdKeyboardDoubleArrowLeft />
//                     </button>
//                   </li>

//                   {generatePageNumbers_hotel().map((page, index) =>
//                     page === "..." ? (
//                       <li
//                         key={index}
//                         className="px-1 h-8 flex items-center justify-center text-gray-500 bg-[#f7f7f7] border-gray-300"
//                       >
//                         <span>...</span>
//                       </li>
//                     ) : (
//                       <li key={index}>
//                         <button
//                           onClick={() => handlePageChange_hotel(page)}
//                           className={`flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 hover:bg-gray-100 hover:text-gray-700 ${
//                             currentPage_hotel === page
//                               ? "text-green-500 underline underline-offset-2"
//                               : ""
//                           }`}
//                         >
//                           {page}
//                         </button>
//                       </li>
//                     ),
//                   )}

//                   <li>
//                     <button
//                       onClick={handleNextPage_hotel}
//                       disabled={currentPage_hotel === totalPages_hotel}
//                       className="flex items-center justify-center px-1 h-8 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700"
//                     >
//                       <MdKeyboardDoubleArrowRight />
//                     </button>
//                   </li>
//                 </ul>
//               </nav>
//             </div>
//           )}
//         </div>
//       </main>
//     </div>
//   );
// };

// export default Properties;

// =========Working code=================
